/**
 * Intelligence Module Page
 * Unified module for:
 * - Signals (Intent Intelligence) - Inbound buying signals from prospects
 * - Opportunities (Program Intelligence) - Outbound market opportunities
 * - Search - Natural language search across intelligence data
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useTabbedChrome } from '@/components/weavein';
import { getWeaveinApiUrl } from '@/src/config';
import { AgentAssistantPanel } from '../../shared/components/AgentAssistant';
import { salesApi, visitorIdentificationApi, leadBuilderApi, marketingApi } from '../../shared/services/platformApi';
import { apiClient } from '@/services/apiClient';
import type { VisitorCompany, CompanyMatch } from '../../shared/services/platformApi';
import {
  Brain,
  Target,
  TrendingUp,
  Users,
  Building2,
  Search,
  Filter,
  Plus,
  Eye,
  Edit,
  Trash2,
  Zap,
  CheckCircle,
  AlertCircle,
  Clock,
  ArrowRight,
  BarChart3,
  RefreshCw,
  Settings,
  Download,
  Database,
  Globe,
  Calendar,
  Activity,
  Layers,
  Radio,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  ChevronRight,
  Loader2,
  MapPin,
  DollarSign,
  X,
  Check,
  Sparkles,
  Flag,
  Send,
  Shield,
  Star,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { tvTokens, TvButton } from '../../shared/components/triveny';
import { config as productConfig } from '@/src/config';

// ============================================================================
// TYPES
// ============================================================================

// Intelligence Module tabs:
// - 'signals' = Intent Intelligence (inbound) - buying signals from prospects
// - 'opportunities' = Program Intelligence (outbound) - market opportunities to pursue
// - 'search' = Natural language search across all intelligence data
// - 'matches' = Signal-to-capability/vendor matches
// - 'vendors'/'capabilities' = Based on single-vendor vs systems-integrator mode
// - 'analytics' = Combined metrics dashboard
// - 'settings' = Module configuration
type IntelligenceTab = 'signals' | 'accounts' | 'opportunities' | 'search' | 'matches' | 'vendors' | 'capabilities' | 'analytics' | 'settings';

// Legacy type alias for backwards compatibility
type IntentTab = IntelligenceTab;

// Organization Mode Types - determines how the module functions
// 'single-vendor' = Organization IS the vendor (e.g., Trust Care offering WeaveIn.ai)
//                   Shows "Capabilities" tab where org defines what THEY offer
// 'systems-integrator' = Organization helps customers find solutions from multiple vendors
//                        Shows "Vendors" tab to manage external vendor ecosystem
type OrganizationMode = 'single-vendor' | 'systems-integrator';

// Capability - represents what the organization offers (single-vendor mode)
interface Capability {
  id: string;
  name: string;
  description: string;
  category: string;
  features: string[];
  useCases: string[];
  industries: string[];
  signalMatches: number;
  status: 'active' | 'draft' | 'deprecated';
}

// Organization Settings for Intent Intelligence
interface OrganizationIntentSettings {
  mode: OrganizationMode;
  organizationName: string;
  organizationDescription?: string;
  primaryIndustries: string[];
  autoMatchEnabled: boolean;
  matchThreshold: number;
}

// ============================================================================
// DATA SOURCE TYPES
// ============================================================================

type DataSourceCategory = 'traffic' | 'external-vendor' | 'self-managed';
type DataSourceStatus = 'connected' | 'disconnected' | 'syncing' | 'error' | 'pending';
type ImportJobStatus = 'completed' | 'failed' | 'running' | 'pending' | 'cancelled';
type ETLStage = 'extraction' | 'transformation' | 'validation' | 'loading' | 'completed' | 'failed';

interface DataSource {
  id: string;
  name: string;
  category: DataSourceCategory;
  type: string;
  description: string;
  status: DataSourceStatus;
  lastSync?: string;
  nextSync?: string;
  recordsImported: number;
  errorMessage?: string;
  config: Record<string, unknown>;
  schedule?: ImportSchedule;
  icon?: string;
}

interface ImportJob {
  id: string;
  dataSourceId: string;
  dataSourceName: string;
  status: ImportJobStatus;
  stage: ETLStage;
  progress: number;
  recordsProcessed: number;
  recordsTotal: number;
  recordsFailed: number;
  startedAt: string;
  completedAt?: string;
  errorMessage?: string;
  logs: ImportJobLog[];
}

interface ImportJobLog {
  timestamp: string;
  level: 'info' | 'warning' | 'error';
  message: string;
  stage: ETLStage;
}

interface ImportSchedule {
  id: string;
  dataSourceId: string;
  frequency: 'realtime' | 'hourly' | 'daily' | 'weekly' | 'monthly' | 'manual';
  lastRun?: string;
  nextRun?: string;
  isActive: boolean;
  timezone: string;
  cronExpression?: string;
}

interface DataSourceType {
  id: string;
  name: string;
  category: DataSourceCategory;
  description: string;
  icon: string;
  requiredFields: DataSourceField[];
  optionalFields: DataSourceField[];
}

interface DataSourceField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url' | 'select' | 'number' | 'checkbox' | 'textarea';
  placeholder?: string;
  options?: { value: string; label: string }[];
  required?: boolean;
  helpText?: string;
}

// Comprehensive list of self-managed data source types
const SELF_MANAGED_DATA_SOURCE_TYPES: DataSourceType[] = [
  // CRM & Sales Data
  {
    id: 'csv-upload',
    name: 'CSV/Excel Upload',
    category: 'self-managed',
    description: 'Upload CSV or Excel files with intent signals, leads, or company data',
    icon: 'file-spreadsheet',
    requiredFields: [
      { key: 'file', label: 'File', type: 'text', placeholder: 'Select file to upload' },
      { key: 'dataType', label: 'Data Type', type: 'select', options: [
        { value: 'leads', label: 'Lead/Company Data' },
        { value: 'signals', label: 'Intent Signals' },
        { value: 'contacts', label: 'Contact Information' },
        { value: 'accounts', label: 'Account Data' },
      ]},
    ],
    optionalFields: [
      { key: 'mappingTemplate', label: 'Field Mapping Template', type: 'select', options: [] },
      { key: 'dedupeField', label: 'Deduplication Field', type: 'text', placeholder: 'e.g., email, domain' },
    ],
  },
  {
    id: 'salesforce',
    name: 'Salesforce CRM',
    category: 'self-managed',
    description: 'Import leads, contacts, opportunities, and account data from Salesforce',
    icon: 'cloud',
    requiredFields: [
      { key: 'instanceUrl', label: 'Instance URL', type: 'url', placeholder: 'https://yourcompany.salesforce.com' },
      { key: 'clientId', label: 'Connected App Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
    optionalFields: [
      { key: 'objects', label: 'Objects to Sync', type: 'textarea', placeholder: 'Lead, Contact, Opportunity, Account' },
      { key: 'filterQuery', label: 'SOQL Filter', type: 'textarea', placeholder: 'WHERE CreatedDate > LAST_N_DAYS:30' },
    ],
  },
  {
    id: 'hubspot',
    name: 'HubSpot CRM',
    category: 'self-managed',
    description: 'Sync contacts, companies, deals, and engagement data from HubSpot',
    icon: 'hub',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [
      { key: 'portalId', label: 'Portal ID', type: 'text' },
      { key: 'objectTypes', label: 'Object Types', type: 'textarea', placeholder: 'contacts, companies, deals' },
    ],
  },
  {
    id: 'pipedrive',
    name: 'Pipedrive',
    category: 'self-managed',
    description: 'Import deals, contacts, and activities from Pipedrive',
    icon: 'pipeline',
    requiredFields: [
      { key: 'apiToken', label: 'API Token', type: 'password' },
    ],
    optionalFields: [
      { key: 'companyDomain', label: 'Company Domain', type: 'text' },
    ],
  },
  {
    id: 'zoho-crm',
    name: 'Zoho CRM',
    category: 'self-managed',
    description: 'Sync leads, contacts, accounts, and deals from Zoho CRM',
    icon: 'database',
    requiredFields: [
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
      { key: 'refreshToken', label: 'Refresh Token', type: 'password' },
    ],
    optionalFields: [],
  },
  // Marketing Automation
  {
    id: 'marketo',
    name: 'Marketo',
    category: 'self-managed',
    description: 'Import leads, activities, and engagement scores from Marketo',
    icon: 'target',
    requiredFields: [
      { key: 'munchkinId', label: 'Munchkin Account ID', type: 'text' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
    optionalFields: [
      { key: 'activityTypes', label: 'Activity Types', type: 'textarea' },
    ],
  },
  {
    id: 'pardot',
    name: 'Pardot',
    category: 'self-managed',
    description: 'Sync prospects, visitor activity, and engagement data from Pardot',
    icon: 'mail',
    requiredFields: [
      { key: 'businessUnitId', label: 'Business Unit ID', type: 'text' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'eloqua',
    name: 'Oracle Eloqua',
    category: 'self-managed',
    description: 'Import contacts, campaign responses, and engagement data from Eloqua',
    icon: 'database',
    requiredFields: [
      { key: 'siteName', label: 'Site Name', type: 'text' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
    ],
    optionalFields: [],
  },
  // Data Enrichment
  {
    id: 'clearbit',
    name: 'Clearbit',
    category: 'self-managed',
    description: 'Enrich company and contact data with firmographic information',
    icon: 'sparkles',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [
      { key: 'enrichmentType', label: 'Enrichment Type', type: 'select', options: [
        { value: 'company', label: 'Company Enrichment' },
        { value: 'person', label: 'Person Enrichment' },
        { value: 'reveal', label: 'Reveal (IP to Company)' },
      ]},
    ],
  },
  {
    id: 'zoominfo',
    name: 'ZoomInfo',
    category: 'self-managed',
    description: 'Import B2B contact and company intelligence data',
    icon: 'users',
    requiredFields: [
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
    ],
    optionalFields: [
      { key: 'searchCriteria', label: 'Search Criteria', type: 'textarea' },
    ],
  },
  {
    id: 'apollo',
    name: 'Apollo.io',
    category: 'self-managed',
    description: 'Import contacts, companies, and engagement data from Apollo',
    icon: 'rocket',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'lusha',
    name: 'Lusha',
    category: 'self-managed',
    description: 'Enrich contact data with direct dial and email information',
    icon: 'phone',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  // Advertising & Campaign Data
  {
    id: 'linkedin-ads',
    name: 'LinkedIn Ads',
    category: 'self-managed',
    description: 'Import campaign performance and lead gen form submissions',
    icon: 'linkedin',
    requiredFields: [
      { key: 'accessToken', label: 'Access Token', type: 'password' },
      { key: 'accountId', label: 'Ad Account ID', type: 'text' },
    ],
    optionalFields: [
      { key: 'campaignIds', label: 'Campaign IDs', type: 'textarea' },
    ],
  },
  {
    id: 'google-ads',
    name: 'Google Ads',
    category: 'self-managed',
    description: 'Import campaign data, conversions, and audience insights',
    icon: 'google',
    requiredFields: [
      { key: 'customerId', label: 'Customer ID', type: 'text' },
      { key: 'developerToken', label: 'Developer Token', type: 'password' },
      { key: 'refreshToken', label: 'OAuth Refresh Token', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'facebook-ads',
    name: 'Facebook/Meta Ads',
    category: 'self-managed',
    description: 'Import ad campaign performance and lead form data',
    icon: 'facebook',
    requiredFields: [
      { key: 'accessToken', label: 'Access Token', type: 'password' },
      { key: 'adAccountId', label: 'Ad Account ID', type: 'text' },
    ],
    optionalFields: [],
  },
  {
    id: 'demandbase',
    name: 'Demandbase ABM',
    category: 'self-managed',
    description: 'Import account-based marketing engagement and intent signals',
    icon: 'target',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'accountId', label: 'Account ID', type: 'text' },
    ],
    optionalFields: [],
  },
  {
    id: '6sense',
    name: '6sense',
    category: 'self-managed',
    description: 'Import account intent data and buying stage predictions',
    icon: 'brain',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  // Web & Product Analytics
  {
    id: 'mixpanel',
    name: 'Mixpanel',
    category: 'self-managed',
    description: 'Import product usage events and user behavior data',
    icon: 'chart-bar',
    requiredFields: [
      { key: 'projectId', label: 'Project ID', type: 'text' },
      { key: 'apiSecret', label: 'API Secret', type: 'password' },
    ],
    optionalFields: [
      { key: 'events', label: 'Events to Import', type: 'textarea' },
    ],
  },
  {
    id: 'amplitude',
    name: 'Amplitude',
    category: 'self-managed',
    description: 'Sync product analytics and user behavior events',
    icon: 'chart-line',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'secretKey', label: 'Secret Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'heap',
    name: 'Heap Analytics',
    category: 'self-managed',
    description: 'Import auto-captured product analytics and user sessions',
    icon: 'layers',
    requiredFields: [
      { key: 'appId', label: 'App ID', type: 'text' },
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'pendo',
    name: 'Pendo',
    category: 'self-managed',
    description: 'Import product analytics, feature usage, and NPS data',
    icon: 'compass',
    requiredFields: [
      { key: 'integrationKey', label: 'Integration Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'fullstory',
    name: 'FullStory',
    category: 'self-managed',
    description: 'Import session replay insights and user frustration signals',
    icon: 'video',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'orgId', label: 'Organization ID', type: 'text' },
    ],
    optionalFields: [],
  },
  // Customer Success & Support
  {
    id: 'zendesk',
    name: 'Zendesk',
    category: 'self-managed',
    description: 'Import support tickets, customer satisfaction, and interaction data',
    icon: 'headphones',
    requiredFields: [
      { key: 'subdomain', label: 'Subdomain', type: 'text', placeholder: 'yourcompany' },
      { key: 'apiToken', label: 'API Token', type: 'password' },
      { key: 'email', label: 'Email', type: 'text' },
    ],
    optionalFields: [],
  },
  {
    id: 'intercom',
    name: 'Intercom',
    category: 'self-managed',
    description: 'Sync conversations, user data, and engagement events',
    icon: 'message-circle',
    requiredFields: [
      { key: 'accessToken', label: 'Access Token', type: 'password' },
    ],
    optionalFields: [
      { key: 'appId', label: 'App ID', type: 'text' },
    ],
  },
  {
    id: 'gainsight',
    name: 'Gainsight',
    category: 'self-managed',
    description: 'Import customer health scores and success metrics',
    icon: 'heart',
    requiredFields: [
      { key: 'accessKey', label: 'Access Key', type: 'password' },
      { key: 'domain', label: 'Domain', type: 'url' },
    ],
    optionalFields: [],
  },
  {
    id: 'freshdesk',
    name: 'Freshdesk',
    category: 'self-managed',
    description: 'Import support tickets and customer interactions',
    icon: 'inbox',
    requiredFields: [
      { key: 'domain', label: 'Domain', type: 'text', placeholder: 'yourcompany.freshdesk.com' },
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  // Data Warehouses & Databases
  {
    id: 'snowflake',
    name: 'Snowflake',
    category: 'self-managed',
    description: 'Query and import data directly from Snowflake data warehouse',
    icon: 'snowflake',
    requiredFields: [
      { key: 'account', label: 'Account Identifier', type: 'text' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
      { key: 'warehouse', label: 'Warehouse', type: 'text' },
      { key: 'database', label: 'Database', type: 'text' },
    ],
    optionalFields: [
      { key: 'schema', label: 'Schema', type: 'text' },
      { key: 'query', label: 'SQL Query', type: 'textarea' },
    ],
  },
  {
    id: 'bigquery',
    name: 'Google BigQuery',
    category: 'self-managed',
    description: 'Import data from BigQuery tables and run custom queries',
    icon: 'database',
    requiredFields: [
      { key: 'projectId', label: 'Project ID', type: 'text' },
      { key: 'credentials', label: 'Service Account JSON', type: 'textarea' },
    ],
    optionalFields: [
      { key: 'dataset', label: 'Dataset', type: 'text' },
      { key: 'query', label: 'SQL Query', type: 'textarea' },
    ],
  },
  {
    id: 'redshift',
    name: 'Amazon Redshift',
    category: 'self-managed',
    description: 'Query and import data from Redshift data warehouse',
    icon: 'database',
    requiredFields: [
      { key: 'host', label: 'Host', type: 'text' },
      { key: 'port', label: 'Port', type: 'number' },
      { key: 'database', label: 'Database', type: 'text' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
    ],
    optionalFields: [
      { key: 'schema', label: 'Schema', type: 'text' },
      { key: 'query', label: 'SQL Query', type: 'textarea' },
    ],
  },
  {
    id: 'postgresql',
    name: 'PostgreSQL',
    category: 'self-managed',
    description: 'Connect to PostgreSQL database for custom data imports',
    icon: 'database',
    requiredFields: [
      { key: 'host', label: 'Host', type: 'text' },
      { key: 'port', label: 'Port', type: 'number' },
      { key: 'database', label: 'Database', type: 'text' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
    ],
    optionalFields: [
      { key: 'sslMode', label: 'SSL Mode', type: 'select', options: [
        { value: 'disable', label: 'Disable' },
        { value: 'require', label: 'Require' },
        { value: 'verify-full', label: 'Verify Full' },
      ]},
      { key: 'query', label: 'SQL Query', type: 'textarea' },
    ],
  },
  {
    id: 'mysql',
    name: 'MySQL',
    category: 'self-managed',
    description: 'Connect to MySQL database for custom data imports',
    icon: 'database',
    requiredFields: [
      { key: 'host', label: 'Host', type: 'text' },
      { key: 'port', label: 'Port', type: 'number' },
      { key: 'database', label: 'Database', type: 'text' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
    ],
    optionalFields: [
      { key: 'query', label: 'SQL Query', type: 'textarea' },
    ],
  },
  // APIs & Webhooks
  {
    id: 'rest-api',
    name: 'REST API',
    category: 'self-managed',
    description: 'Connect to any REST API endpoint to import data',
    icon: 'code',
    requiredFields: [
      { key: 'baseUrl', label: 'Base URL', type: 'url' },
      { key: 'endpoint', label: 'Endpoint Path', type: 'text' },
      { key: 'method', label: 'HTTP Method', type: 'select', options: [
        { value: 'GET', label: 'GET' },
        { value: 'POST', label: 'POST' },
      ]},
    ],
    optionalFields: [
      { key: 'headers', label: 'Headers (JSON)', type: 'textarea' },
      { key: 'authType', label: 'Authentication', type: 'select', options: [
        { value: 'none', label: 'None' },
        { value: 'bearer', label: 'Bearer Token' },
        { value: 'basic', label: 'Basic Auth' },
        { value: 'apiKey', label: 'API Key' },
      ]},
      { key: 'authValue', label: 'Auth Value', type: 'password' },
      { key: 'bodyTemplate', label: 'Request Body', type: 'textarea' },
      { key: 'responseMapping', label: 'Response Mapping (JSON Path)', type: 'textarea' },
    ],
  },
  {
    id: 'graphql-api',
    name: 'GraphQL API',
    category: 'self-managed',
    description: 'Connect to GraphQL APIs for flexible data queries',
    icon: 'code',
    requiredFields: [
      { key: 'endpoint', label: 'GraphQL Endpoint', type: 'url' },
      { key: 'query', label: 'GraphQL Query', type: 'textarea' },
    ],
    optionalFields: [
      { key: 'variables', label: 'Query Variables (JSON)', type: 'textarea' },
      { key: 'headers', label: 'Headers (JSON)', type: 'textarea' },
    ],
  },
  {
    id: 'webhook',
    name: 'Webhook Receiver',
    category: 'self-managed',
    description: 'Receive real-time data via incoming webhooks',
    icon: 'webhook',
    requiredFields: [
      { key: 'webhookName', label: 'Webhook Name', type: 'text' },
    ],
    optionalFields: [
      { key: 'secretKey', label: 'Webhook Secret (for verification)', type: 'password' },
      { key: 'dataMapping', label: 'Data Field Mapping', type: 'textarea' },
    ],
  },
  // File & Cloud Storage
  {
    id: 's3-bucket',
    name: 'Amazon S3',
    category: 'self-managed',
    description: 'Import files from S3 buckets automatically',
    icon: 'cloud',
    requiredFields: [
      { key: 'bucketName', label: 'Bucket Name', type: 'text' },
      { key: 'accessKeyId', label: 'Access Key ID', type: 'text' },
      { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password' },
      { key: 'region', label: 'Region', type: 'text' },
    ],
    optionalFields: [
      { key: 'prefix', label: 'Key Prefix', type: 'text' },
      { key: 'filePattern', label: 'File Pattern', type: 'text', placeholder: '*.csv' },
    ],
  },
  {
    id: 'google-cloud-storage',
    name: 'Google Cloud Storage',
    category: 'self-managed',
    description: 'Import files from GCS buckets',
    icon: 'cloud',
    requiredFields: [
      { key: 'bucketName', label: 'Bucket Name', type: 'text' },
      { key: 'credentials', label: 'Service Account JSON', type: 'textarea' },
    ],
    optionalFields: [
      { key: 'prefix', label: 'Object Prefix', type: 'text' },
    ],
  },
  {
    id: 'azure-blob',
    name: 'Azure Blob Storage',
    category: 'self-managed',
    description: 'Import files from Azure Blob containers',
    icon: 'cloud',
    requiredFields: [
      { key: 'accountName', label: 'Storage Account Name', type: 'text' },
      { key: 'containerName', label: 'Container Name', type: 'text' },
      { key: 'connectionString', label: 'Connection String', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'sftp',
    name: 'SFTP Server',
    category: 'self-managed',
    description: 'Import files from SFTP/FTP servers',
    icon: 'folder',
    requiredFields: [
      { key: 'host', label: 'Host', type: 'text' },
      { key: 'port', label: 'Port', type: 'number' },
      { key: 'username', label: 'Username', type: 'text' },
      { key: 'password', label: 'Password', type: 'password' },
    ],
    optionalFields: [
      { key: 'remotePath', label: 'Remote Path', type: 'text' },
      { key: 'filePattern', label: 'File Pattern', type: 'text' },
    ],
  },
  // Survey & Feedback
  {
    id: 'typeform',
    name: 'Typeform',
    category: 'self-managed',
    description: 'Import survey responses and form submissions',
    icon: 'clipboard',
    requiredFields: [
      { key: 'accessToken', label: 'Access Token', type: 'password' },
      { key: 'formId', label: 'Form ID', type: 'text' },
    ],
    optionalFields: [],
  },
  {
    id: 'surveymonkey',
    name: 'SurveyMonkey',
    category: 'self-managed',
    description: 'Import survey responses and feedback data',
    icon: 'clipboard',
    requiredFields: [
      { key: 'accessToken', label: 'Access Token', type: 'password' },
    ],
    optionalFields: [
      { key: 'surveyIds', label: 'Survey IDs', type: 'textarea' },
    ],
  },
  {
    id: 'nps-survey',
    name: 'NPS/CSAT Surveys',
    category: 'self-managed',
    description: 'Import Net Promoter Score and satisfaction survey data',
    icon: 'star',
    requiredFields: [
      { key: 'provider', label: 'Survey Provider', type: 'select', options: [
        { value: 'delighted', label: 'Delighted' },
        { value: 'promoter', label: 'Promoter.io' },
        { value: 'satismeter', label: 'SatisMeter' },
        { value: 'wootric', label: 'Wootric' },
        { value: 'other', label: 'Other' },
      ]},
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  // Social & Community
  {
    id: 'twitter-x',
    name: 'Twitter/X',
    category: 'self-managed',
    description: 'Import mentions, hashtags, and social signals',
    icon: 'twitter',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'apiSecret', label: 'API Secret', type: 'password' },
      { key: 'accessToken', label: 'Access Token', type: 'password' },
      { key: 'accessTokenSecret', label: 'Access Token Secret', type: 'password' },
    ],
    optionalFields: [
      { key: 'searchTerms', label: 'Search Terms/Hashtags', type: 'textarea' },
    ],
  },
  {
    id: 'g2-reviews',
    name: 'G2 Reviews',
    category: 'self-managed',
    description: 'Import G2 reviews and competitor intelligence',
    icon: 'star',
    requiredFields: [
      { key: 'apiToken', label: 'API Token', type: 'password' },
    ],
    optionalFields: [
      { key: 'productIds', label: 'Product IDs', type: 'textarea' },
    ],
  },
  {
    id: 'trustradius',
    name: 'TrustRadius',
    category: 'self-managed',
    description: 'Import product reviews and buyer intent signals',
    icon: 'star',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  // Event & Webinar
  {
    id: 'eventbrite',
    name: 'Eventbrite',
    category: 'self-managed',
    description: 'Import event registrations and attendee data',
    icon: 'calendar',
    requiredFields: [
      { key: 'privateToken', label: 'Private Token', type: 'password' },
    ],
    optionalFields: [
      { key: 'organizationId', label: 'Organization ID', type: 'text' },
    ],
  },
  {
    id: 'zoom-webinar',
    name: 'Zoom Webinars',
    category: 'self-managed',
    description: 'Import webinar registrations and attendance data',
    icon: 'video',
    requiredFields: [
      { key: 'accountId', label: 'Account ID', type: 'text' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'on24',
    name: 'ON24',
    category: 'self-managed',
    description: 'Import webinar engagement and attendee analytics',
    icon: 'presentation',
    requiredFields: [
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
    optionalFields: [],
  },
  // Custom & Other
  {
    id: 'segment',
    name: 'Segment',
    category: 'self-managed',
    description: 'Import customer data from Segment CDP',
    icon: 'layers',
    requiredFields: [
      { key: 'writeKey', label: 'Write Key', type: 'password' },
    ],
    optionalFields: [
      { key: 'sourceId', label: 'Source ID', type: 'text' },
    ],
  },
  {
    id: 'rudderstack',
    name: 'RudderStack',
    category: 'self-managed',
    description: 'Import customer data from RudderStack CDP',
    icon: 'layers',
    requiredFields: [
      { key: 'dataPlaneUrl', label: 'Data Plane URL', type: 'url' },
      { key: 'writeKey', label: 'Write Key', type: 'password' },
    ],
    optionalFields: [],
  },
];

// Traffic data source types
const TRAFFIC_DATA_SOURCE_TYPES: DataSourceType[] = [
  {
    id: 'google-analytics',
    name: 'Google Analytics 4',
    category: 'traffic',
    description: 'Import website traffic, user behavior, and conversion data',
    icon: 'chart-line',
    requiredFields: [
      { key: 'propertyId', label: 'Property ID', type: 'text' },
      { key: 'credentials', label: 'Service Account JSON', type: 'textarea' },
    ],
    optionalFields: [
      { key: 'dimensions', label: 'Dimensions', type: 'textarea' },
      { key: 'metrics', label: 'Metrics', type: 'textarea' },
    ],
  },
  {
    id: 'adobe-analytics',
    name: 'Adobe Analytics',
    category: 'traffic',
    description: 'Import web analytics and digital marketing data',
    icon: 'chart-bar',
    requiredFields: [
      { key: 'companyId', label: 'Company ID', type: 'text' },
      { key: 'clientId', label: 'Client ID', type: 'text' },
      { key: 'clientSecret', label: 'Client Secret', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'website-tracking',
    name: 'Website Tracking Pixel',
    category: 'traffic',
    description: 'First-party tracking pixel for visitor identification',
    icon: 'eye',
    requiredFields: [
      { key: 'domain', label: 'Website Domain', type: 'text' },
    ],
    optionalFields: [
      { key: 'excludePaths', label: 'Exclude URL Paths', type: 'textarea' },
      { key: 'ipEnrichment', label: 'Enable IP-to-Company Enrichment', type: 'checkbox' },
    ],
  },
  {
    id: 'hotjar',
    name: 'Hotjar',
    category: 'traffic',
    description: 'Import heatmaps, recordings, and user feedback',
    icon: 'flame',
    requiredFields: [
      { key: 'siteId', label: 'Site ID', type: 'text' },
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'clarity',
    name: 'Microsoft Clarity',
    category: 'traffic',
    description: 'Import session recordings and heatmap data',
    icon: 'search',
    requiredFields: [
      { key: 'projectId', label: 'Project ID', type: 'text' },
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
];

// External intent vendor types
const EXTERNAL_VENDOR_DATA_SOURCE_TYPES: DataSourceType[] = [
  {
    id: 'bombora',
    name: 'Bombora',
    category: 'external-vendor',
    description: 'B2B intent data from content consumption across publisher co-op',
    icon: 'radar',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'accountId', label: 'Account ID', type: 'text' },
    ],
    optionalFields: [
      { key: 'topics', label: 'Topic Filters', type: 'textarea' },
      { key: 'companySize', label: 'Company Size Filter', type: 'select', options: [
        { value: 'all', label: 'All Sizes' },
        { value: 'enterprise', label: 'Enterprise (1000+)' },
        { value: 'mid-market', label: 'Mid-Market (100-999)' },
        { value: 'smb', label: 'SMB (1-99)' },
      ]},
    ],
  },
  {
    id: 'g2-intent',
    name: 'G2 Buyer Intent',
    category: 'external-vendor',
    description: 'Intent signals from software comparison and research activity',
    icon: 'shopping-cart',
    requiredFields: [
      { key: 'apiToken', label: 'API Token', type: 'password' },
    ],
    optionalFields: [
      { key: 'categories', label: 'Software Categories', type: 'textarea' },
    ],
  },
  {
    id: 'trustradius-intent',
    name: 'TrustRadius Intent',
    category: 'external-vendor',
    description: 'Buyer research intent from product comparisons and reviews',
    icon: 'target',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'technographics',
    name: 'Technographic Data',
    category: 'external-vendor',
    description: 'Technology stack data for target accounts',
    icon: 'cpu',
    requiredFields: [
      { key: 'provider', label: 'Data Provider', type: 'select', options: [
        { value: 'builtwith', label: 'BuiltWith' },
        { value: 'similartech', label: 'SimilarTech' },
        { value: 'hgdata', label: 'HG Insights' },
        { value: 'datanyze', label: 'Datanyze' },
      ]},
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [
      { key: 'technologies', label: 'Technologies to Track', type: 'textarea' },
    ],
  },
  {
    id: 'leadiq',
    name: 'LeadIQ',
    category: 'external-vendor',
    description: 'Prospecting data with contact information and buying signals',
    icon: 'users',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'demandbase-intent',
    name: 'Demandbase Intent',
    category: 'external-vendor',
    description: 'Account-level intent signals and engagement data',
    icon: 'activity',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
      { key: 'accountId', label: 'Account ID', type: 'text' },
    ],
    optionalFields: [],
  },
  {
    id: '6sense-intent',
    name: '6sense Intent',
    category: 'external-vendor',
    description: 'AI-powered buying stage predictions and intent signals',
    icon: 'brain',
    requiredFields: [
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
  {
    id: 'bidstream',
    name: 'Bidstream Data',
    category: 'external-vendor',
    description: 'Intent data from programmatic advertising signals',
    icon: 'radio',
    requiredFields: [
      { key: 'provider', label: 'Provider', type: 'select', options: [
        { value: 'eyeota', label: 'Eyeota' },
        { value: 'oracle', label: 'Oracle Data Cloud' },
        { value: 'nielsen', label: 'Nielsen' },
      ]},
      { key: 'apiKey', label: 'API Key', type: 'password' },
    ],
    optionalFields: [],
  },
];

// Initial data sources

interface BuyingSignal {
  id: string;
  company: string;
  signalType: 'website_visit' | 'content_download' | 'pricing_page' | 'demo_request' | 'competitor_research' | 'job_posting';
  strength: 'hot' | 'warm' | 'cold';
  category: string;
  timestamp: string;
  source: string;
  details: string;
}

interface VendorMatch {
  id: string;
  signal: BuyingSignal;
  vendor: Vendor;
  matchScore: number;
  matchReasons: string[];
  status: 'pending' | 'contacted' | 'qualified' | 'disqualified';
  assignedTo?: string;
}

interface Vendor {
  id: string;
  name: string;
  category: string;
  capabilities: string[];
  rating: number;
  activeDeals: number;
}

interface IntentRule {
  id: string;
  name: string;
  trigger: string;
  conditions: string[];
  actions: string[];
  status: 'active' | 'paused';
  matches: number;
}

// ============================================================================
// INITIAL DATA
// ============================================================================

// Helper to generate dynamic timestamps relative to current date
const getRelativeTimestamp = (daysAgo: number, hoursAgo: number = 0): string => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  date.setHours(date.getHours() - hoursAgo);
  return date.toISOString();
};

// Helper to get a relative date string (YYYY-MM-DD) — used for default
// close dates when promoting an opportunity to the sales pipeline.
const getRelativeDate = (daysFromNow: number): string => {
  const date = new Date();
  date.setDate(date.getDate() + daysFromNow);
  return date.toISOString().split('T')[0];
};


// WeaveIn (isTriveny=false) default org settings. Restored: it was accidentally
// removed with the mock arrays in the de-mock change, causing a ReferenceError
// that crashed the whole Intelligence page on WeaveIn prod (app.weavein.ai) —
// the orgSettings useState initializer references it on the non-Triveny path.
const defaultOrganizationSettings: OrganizationIntentSettings = {
  mode: 'single-vendor',
  organizationName: 'Trust Care',
  organizationDescription: 'WeaveIn.ai - Enterprise platform for healthcare, SDOH, and employee benefits',
  primaryIndustries: ['Healthcare', 'Insurance', 'Employee Benefits', 'SDOH'],
  autoMatchEnabled: true,
  matchThreshold: 70,
};

const trivenyDefaultSettings: OrganizationIntentSettings = {
  mode: 'single-vendor',
  organizationName: 'Triveny Demo Co',
  organizationDescription: 'Triveny.ai — AI-native marketing and sales platform for B2B teams',
  primaryIndustries: ['B2B SaaS', 'B2B Services', 'GovTech', 'Marketplaces'],
  autoMatchEnabled: true,
  matchThreshold: 70,
};

// ============================================================================
// REAL SIGNAL MAPPERS
// ============================================================================
// Convert backend rows (Visitor ID + Lead Builder ICP matches) into the
// shape the Signals tab renders. Strength buckets the underlying numeric
// score; details summarize the source-specific evidence so the row stays
// scannable when scrolled together with the curated mock signals.

function strengthFromScore(score: number | undefined | null): BuyingSignal['strength'] {
  const s = typeof score === 'number' ? score : 0;
  if (s >= 70) return 'hot';
  if (s >= 40) return 'warm';
  return 'cold';
}

function mapVisitorCompanyToSignal(c: VisitorCompany): BuyingSignal {
  const score = Math.max(c.intent_score || 0, c.fit_score || 0);
  // page-view density is a stronger pricing-page indicator than raw visits
  const isPricing = c.total_page_views >= 8 && (c.intent_score || 0) >= 60;
  return {
    id: `visitor-${c.id}`,
    company: c.company_name || c.domain || 'Unknown',
    signalType: isPricing ? 'pricing_page' : 'website_visit',
    strength: strengthFromScore(score),
    category: c.industry || c.industry_group || 'B2B',
    timestamp: c.last_seen_at || c.first_seen_at || new Date().toISOString(),
    source: 'Visitor ID',
    details: [
      c.total_visitors > 0 && `${c.total_visitors} visitor${c.total_visitors === 1 ? '' : 's'}`,
      c.total_visits > 0 && `${c.total_visits} session${c.total_visits === 1 ? '' : 's'}`,
      c.total_page_views > 0 && `${c.total_page_views} page views`,
      c.employee_count && `~${c.employee_count.toLocaleString()} employees`,
      c.headquarters_state && c.headquarters_state,
      `intent ${c.intent_score ?? 0}/100`,
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

function mapCompanyMatchToSignal(m: CompanyMatch): BuyingSignal {
  // demo_request is reserved for the highest-confidence ICP matches; warm
  // matches surface as content_download to indicate evaluation interest
  // without overstating buyer intent.
  const signalType: BuyingSignal['signalType'] =
    (m.score || 0) >= 80 ? 'demo_request' : 'content_download';
  return {
    id: `icp-${m.id}`,
    company: m.company_name || m.domain || 'Unknown',
    signalType,
    strength: strengthFromScore(m.score),
    category: m.industry || m.sub_industry || 'B2B',
    timestamp: m.updated_at || m.created_at || new Date().toISOString(),
    source: 'ICP Match — Lead Builder',
    details: [
      `ICP score ${m.score ?? 0}/100`,
      m.tier !== undefined && `Tier ${m.tier}`,
      m.employee_count && `~${m.employee_count.toLocaleString()} employees`,
      m.headquarters_state && m.headquarters_state,
      m.match_reasons && m.match_reasons.length > 0 && m.match_reasons.slice(0, 2).join(', '),
    ]
      .filter(Boolean)
      .join(' · '),
  };
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function getSignalIcon(type: BuyingSignal['signalType']) {
  switch (type) {
    case 'pricing_page':
      return <Target className="h-4 w-4 text-green-600" />;
    case 'content_download':
      return <Download className="h-4 w-4 text-blue-600" />;
    case 'demo_request':
      return <Zap className="h-4 w-4 text-purple-600" />;
    case 'competitor_research':
      return <Search className="h-4 w-4 text-orange-600" />;
    case 'job_posting':
      return <Users className="h-4 w-4 text-gray-600" />;
    default:
      return <Eye className="h-4 w-4 text-gray-600" />;
  }
}

function getStrengthBadge(strength: BuyingSignal['strength']) {
  switch (strength) {
    case 'hot':
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Hot</span>;
    case 'warm':
      return <span className="px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">Warm</span>;
    case 'cold':
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Cold</span>;
  }
}

function getStatusBadge(status: VendorMatch['status']) {
  switch (status) {
    case 'pending':
      return <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium">Pending</span>;
    case 'contacted':
      return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">Contacted</span>;
    case 'qualified':
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">Qualified</span>;
    case 'disqualified':
      return <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">Disqualified</span>;
  }
}

// ============================================================================
// MODAL COMPONENTS
// ============================================================================

interface CreateRuleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rule: Omit<IntentRule, 'id' | 'matches'>) => void;
}

function CreateRuleModal({ isOpen, onClose, onSubmit }: CreateRuleModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [trigger, setTrigger] = useState('');
  const [conditions, setConditions] = useState('');
  const [actions, setActions] = useState('');

  const triggerOptions = [
    'Signal strength = Hot',
    'Signal strength = Warm',
    'Signal type = Demo Request',
    'Signal type = Pricing Page',
    'Signal type = Content Download',
    'Signal type = Competitor Research',
    'New company identified',
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      trigger,
      conditions: conditions.split('\n').filter(c => c.trim()),
      actions: actions.split('\n').filter(a => a.trim()),
      status: 'active',
    });
    setName('');
    setTrigger('');
    setConditions('');
    setActions('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dialogs.createRule.title', 'Create Matching Rule')}</DialogTitle>
          <DialogDescription>
            {t('platform.intelligence.dialogs.createRule.description', 'Define rules to automatically match buying signals with vendors.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.dialogs.createRule.ruleName', 'Rule Name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('platform.intelligence.dialogs.createRule.ruleNamePlaceholder', 'e.g., High-Value Lead Auto-Match')}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.dialogs.createRule.trigger', 'Trigger')} *
            </label>
            <select
              value={trigger}
              onChange={(e) => setTrigger(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('platform.intelligence.dialogs.createRule.selectTrigger', 'Select a trigger...')}</option>
              {triggerOptions.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.dialogs.createRule.conditions', 'Conditions (one per line)')}
            </label>
            <textarea
              value={conditions}
              onChange={(e) => setConditions(e.target.value)}
              placeholder={t('platform.intelligence.dialogs.createRule.conditionsPlaceholder', 'e.g., Company size > 100\nIndustry in target list')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.dialogs.createRule.actions', 'Actions (one per line)')} *
            </label>
            <textarea
              value={actions}
              onChange={(e) => setActions(e.target.value)}
              placeholder={t('platform.intelligence.dialogs.createRule.actionsPlaceholder', 'e.g., Create vendor match\nNotify sales team\nAdd to CRM')}
              rows={3}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.intelligence.dialogs.createRule.submit', 'Create Rule')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface CreateManualMatchModalProps {
  isOpen: boolean;
  onClose: () => void;
  signals: BuyingSignal[];
  vendors: Vendor[];
  onSubmit: (signalId: string, vendorId: string, notes: string) => void;
  preselectedSignal?: BuyingSignal | null;
}

function CreateManualMatchModal({ isOpen, onClose, signals, vendors, onSubmit, preselectedSignal }: CreateManualMatchModalProps) {
  const { t } = useAppTranslation();
  const [selectedSignal, setSelectedSignal] = useState(preselectedSignal?.id || '');
  const [selectedVendor, setSelectedVendor] = useState('');
  const [notes, setNotes] = useState('');

  React.useEffect(() => {
    if (preselectedSignal) {
      setSelectedSignal(preselectedSignal.id);
    }
  }, [preselectedSignal]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(selectedSignal, selectedVendor, notes);
    setSelectedSignal('');
    setSelectedVendor('');
    setNotes('');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dialogs.createMatch.title', 'Create Manual Match')}</DialogTitle>
          <DialogDescription>
            {t('platform.intelligence.dialogs.createMatch.description', 'Manually match a buying signal with a vendor.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.dialogs.createMatch.buyingSignal', 'Buying Signal')} *
            </label>
            <select
              value={selectedSignal}
              onChange={(e) => setSelectedSignal(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('platform.intelligence.dialogs.createMatch.selectSignal', 'Select a signal...')}</option>
              {signals.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.company} - {s.signalType.replace('_', ' ')}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.dialogs.createMatch.vendor', 'Vendor')} *
            </label>
            <select
              value={selectedVendor}
              onChange={(e) => setSelectedVendor(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">{t('platform.intelligence.dialogs.createMatch.selectVendor', 'Select a vendor...')}</option>
              {vendors.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} ({v.category})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.dialogs.createMatch.matchNotes', 'Match Notes')}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('platform.intelligence.dialogs.createMatch.matchNotesPlaceholder', 'Add notes about why this is a good match...')}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.intelligence.dialogs.createMatch.submit', 'Create Match')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface ViewSignalModalProps {
  isOpen: boolean;
  onClose: () => void;
  signal: BuyingSignal | null;
}

function ViewSignalModal({ isOpen, onClose, signal }: ViewSignalModalProps) {
  const { t } = useAppTranslation();
  if (!signal) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dialogs.viewSignal.title', 'Signal Details')}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-4">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-gray-100 rounded-lg">
              {getSignalIcon(signal.signalType)}
            </div>
            <div>
              <h3 className="font-semibold text-lg">{signal.company}</h3>
              <p className="text-gray-500">{signal.category}</p>
            </div>
            {getStrengthBadge(signal.strength)}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">{t('platform.intelligence.signals.signalType', 'Signal Type')}</label>
              <p className="text-gray-900 capitalize">{signal.signalType.replace('_', ' ')}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">{t('platform.intelligence.signals.source', 'Source')}</label>
              <p className="text-gray-900">{signal.source}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">{t('platform.intelligence.signals.timestamp', 'Timestamp')}</label>
              <p className="text-gray-900">{new Date(signal.timestamp).toLocaleString()}</p>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500 uppercase">{t('platform.intelligence.signals.strength', 'Strength')}</label>
              <p className="text-gray-900 capitalize">{signal.strength}</p>
            </div>
          </div>

          <div>
            <label className="text-xs font-medium text-gray-500 uppercase">{t('platform.intelligence.signals.details', 'Details')}</label>
            <p className="text-gray-900 bg-gray-50 p-3 rounded-lg mt-1">{signal.details}</p>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.intelligence.common.close', 'Close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface AddVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (vendor: Omit<Vendor, 'id' | 'activeDeals'>) => void;
}

function AddVendorModal({ isOpen, onClose, onSubmit }: AddVendorModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [rating, setRating] = useState('4.5');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      category,
      capabilities: capabilities.split(',').map(c => c.trim()).filter(c => c),
      rating: parseFloat(rating),
    });
    setName('');
    setCategory('');
    setCapabilities('');
    setRating('4.5');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dialogs.addVendor.title', 'Add New Vendor')}</DialogTitle>
          <DialogDescription>
            {t('platform.intelligence.dialogs.addVendor.description', 'Add a new vendor to match with buying signals.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.vendorName', 'Vendor Name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('platform.intelligence.vendors.vendorNamePlaceholder', 'e.g., Acme Solutions')}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.category', 'Category')} *
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              placeholder={t('platform.intelligence.vendors.categoryPlaceholder', 'e.g., CRM Platform')}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.capabilitiesLabel', 'Capabilities (comma-separated)')} *
            </label>
            <input
              type="text"
              value={capabilities}
              onChange={(e) => setCapabilities(e.target.value)}
              placeholder={t('platform.intelligence.vendors.capabilitiesPlaceholder', 'e.g., Sales Analytics, Marketing Automation, Reporting')}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.rating', 'Rating (1-5)')}
            </label>
            <input
              type="number"
              min="1"
              max="5"
              step="0.1"
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.intelligence.dialogs.addVendor.submit', 'Add Vendor')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface EditVendorModalProps {
  isOpen: boolean;
  onClose: () => void;
  vendor: Vendor | null;
  onSubmit: (vendor: Vendor) => void;
}

function EditVendorModal({ isOpen, onClose, vendor, onSubmit }: EditVendorModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [category, setCategory] = useState('');
  const [capabilities, setCapabilities] = useState('');
  const [rating, setRating] = useState('4.5');

  React.useEffect(() => {
    if (vendor) {
      setName(vendor.name);
      setCategory(vendor.category);
      setCapabilities(vendor.capabilities.join(', '));
      setRating(String(vendor.rating));
    }
  }, [vendor]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (vendor) {
      onSubmit({
        ...vendor,
        name,
        category,
        capabilities: capabilities.split(',').map(c => c.trim()).filter(c => c),
        rating: parseFloat(rating),
      });
    }
    onClose();
  };

  if (!vendor) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dialogs.editVendor.title', 'Edit Vendor')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.vendorName', 'Vendor Name')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.category', 'Category')} *
            </label>
            <input
              type="text"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.capabilitiesLabel', 'Capabilities (comma-separated)')} *
            </label>
            <input
              type="text"
              value={capabilities}
              onChange={(e) => setCapabilities(e.target.value)}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('platform.intelligence.vendors.rating', 'Rating (1-5)')}
            </label>
            <input
              type="number"
              min="1"
              max="5"
              step="0.1"
              value={rating}
              onChange={(e) => setRating(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.intelligence.common.saveChanges', 'Save Changes')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// CAPABILITY MODALS (Single-Vendor Mode)
// ============================================================================

interface AddCapabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (capability: Omit<Capability, 'id' | 'signalMatches'>) => void;
}

function AddCapabilityModal({ isOpen, onClose, onSubmit }: AddCapabilityModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [features, setFeatures] = useState('');
  const [useCases, setUseCases] = useState('');
  const [industries, setIndustries] = useState('');
  const [status, setStatus] = useState<Capability['status']>('active');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      name,
      description,
      category,
      features: features.split(',').map(f => f.trim()).filter(f => f),
      useCases: useCases.split(',').map(u => u.trim()).filter(u => u),
      industries: industries.split(',').map(i => i.trim()).filter(i => i),
      status,
    });
    // Reset form
    setName('');
    setDescription('');
    setCategory('');
    setFeatures('');
    setUseCases('');
    setIndustries('');
    setStatus('active');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dialogs.addCapability.title', 'Add New Capability')}</DialogTitle>
          <DialogDescription>
            {t('platform.intelligence.dialogs.addCapability.description', 'Define a product or service capability to match with buying signals.')}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.capabilityName', 'Capability Name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('platform.intelligence.capabilities.capabilityNamePlaceholder', 'e.g., Benefits Administration Platform')}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.category', 'Category')} *
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                placeholder={t('platform.intelligence.capabilities.categoryPlaceholder', 'e.g., Healthcare Technology')}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.status', 'Status')}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Capability['status'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="active">{t('platform.intelligence.capabilities.statusActive', 'Active')}</option>
                <option value="draft">{t('platform.intelligence.capabilities.statusDraft', 'Draft')}</option>
                <option value="deprecated">{t('platform.intelligence.capabilities.statusDeprecated', 'Deprecated')}</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.description', 'Description')} *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('platform.intelligence.capabilities.descriptionPlaceholder', 'Brief description of this capability...')}
                required
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.keyFeatures', 'Key Features (comma-separated)')} *
              </label>
              <input
                type="text"
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
                placeholder={t('platform.intelligence.capabilities.keyFeaturesPlaceholder', 'e.g., Open enrollment, Plan comparison, Self-service portal')}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.useCases', 'Use Cases (comma-separated)')}
              </label>
              <input
                type="text"
                value={useCases}
                onChange={(e) => setUseCases(e.target.value)}
                placeholder={t('platform.intelligence.capabilities.useCasesPlaceholder', 'e.g., Annual enrollment, Benefits communication, Life events')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.targetIndustries', 'Target Industries (comma-separated)')}
              </label>
              <input
                type="text"
                value={industries}
                onChange={(e) => setIndustries(e.target.value)}
                placeholder={t('platform.intelligence.capabilities.targetIndustriesPlaceholder', 'e.g., Healthcare, Insurance, Employee Benefits')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {t('platform.intelligence.dialogs.addCapability.submit', 'Add Capability')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface EditCapabilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  capability: Capability | null;
  onSubmit: (capability: Capability) => void;
}

function EditCapabilityModal({ isOpen, onClose, capability, onSubmit }: EditCapabilityModalProps) {
  const { t } = useAppTranslation();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [features, setFeatures] = useState('');
  const [useCases, setUseCases] = useState('');
  const [industries, setIndustries] = useState('');
  const [status, setStatus] = useState<Capability['status']>('active');

  React.useEffect(() => {
    if (capability) {
      setName(capability.name);
      setDescription(capability.description);
      setCategory(capability.category);
      setFeatures(capability.features.join(', '));
      setUseCases(capability.useCases.join(', '));
      setIndustries(capability.industries.join(', '));
      setStatus(capability.status);
    }
  }, [capability]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (capability) {
      onSubmit({
        ...capability,
        name,
        description,
        category,
        features: features.split(',').map(f => f.trim()).filter(f => f),
        useCases: useCases.split(',').map(u => u.trim()).filter(u => u),
        industries: industries.split(',').map(i => i.trim()).filter(i => i),
        status,
      });
    }
    onClose();
  };

  if (!capability) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dialogs.editCapability.title', 'Edit Capability')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="col-span-1 sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.capabilityName', 'Capability Name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.category', 'Category')} *
              </label>
              <input
                type="text"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.status', 'Status')}
              </label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as Capability['status'])}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                <option value="active">{t('platform.intelligence.capabilities.statusActive', 'Active')}</option>
                <option value="draft">{t('platform.intelligence.capabilities.statusDraft', 'Draft')}</option>
                <option value="deprecated">{t('platform.intelligence.capabilities.statusDeprecated', 'Deprecated')}</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.description', 'Description')} *
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                required
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.keyFeatures', 'Key Features (comma-separated)')} *
              </label>
              <input
                type="text"
                value={features}
                onChange={(e) => setFeatures(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.useCases', 'Use Cases (comma-separated)')}
              </label>
              <input
                type="text"
                value={useCases}
                onChange={(e) => setUseCases(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.capabilities.targetIndustries', 'Target Industries (comma-separated)')}
              </label>
              <input
                type="text"
                value={industries}
                onChange={(e) => setIndustries(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              />
            </div>
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              {t('platform.intelligence.common.saveChanges', 'Save Changes')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// TAB COMPONENTS
// ============================================================================

// Accounts tab — agentic account scoring. Intent signals rolled up per company
// into a composite score (weighted by signal type × recency) with hot/warm/cold
// tiers. Recomputes server-side on read. The account-scoring celery agent also
// recomputes every 30 min and fires a surge notification when an account turns hot.
interface AccountScore {
  company: string;
  score: number;
  tier: string;
  signal_count: number;
  top_signals: string[];
  last_signal_at?: string | null;
}

interface NbaStep {
  channel: string;
  subject?: string | null;
  content: string;
  delay_hours: number;
}
interface NbaRecommendation {
  id: string;
  company: string;
  status: string;
  rationale?: string;
  steps: NbaStep[];
  model?: string;
  score_at_draft?: number;
  signal_count?: number;
}

function AccountScoresTab() {
  const [accounts, setAccounts] = useState<AccountScore[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [recs, setRecs] = useState<NbaRecommendation[]>([]);
  const [generating, setGenerating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [promoting, setPromoting] = useState<string | null>(null);
  const [promoted, setPromoted] = useState<Set<string>>(new Set());

  const loadRecs = useCallback(async () => {
    try {
      const res: any = await marketingApi.getNbaRecommendations('pending');
      setRecs(Array.isArray(res) ? res : res?.data || []);
    } catch {
      setRecs([]);
    }
  }, []);

  const load = useCallback(async () => {
    try {
      const res: any = await marketingApi.getAccountScores(50);
      const rows: AccountScore[] = Array.isArray(res) ? res : res?.data || res?.accounts || [];
      setAccounts(rows);
    } catch {
      setAccounts([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void load();
    void loadRecs();
  }, [load, loadRecs]);

  const handleGenerate = async (company?: string) => {
    setGenerating(true);
    setBanner(null);
    try {
      const res: any = await marketingApi.generateNbaRecommendations(company ? { company } : { limit: 5 });
      const created = res?.created ?? 0;
      setBanner(
        created > 0
          ? `Drafted ${created} outreach ${created === 1 ? 'sequence' : 'sequences'} — review below.`
          : 'No new drafts — hot accounts already have a pending recommendation.'
      );
      await loadRecs();
    } catch {
      setBanner('Could not generate recommendations. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const handleApprove = async (rec: NbaRecommendation) => {
    setBusyId(rec.id);
    try {
      await marketingApi.approveNbaRecommendation(rec.id);
      setBanner(`Approved — a draft campaign for ${rec.company} was created. Add an audience and launch it from Campaigns.`);
      setRecs((prev) => prev.filter((r) => r.id !== rec.id));
    } catch {
      setBanner('Could not approve. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handleReject = async (rec: NbaRecommendation) => {
    setBusyId(rec.id);
    try {
      await marketingApi.rejectNbaRecommendation(rec.id);
      setRecs((prev) => prev.filter((r) => r.id !== rec.id));
    } catch {
      setBanner('Could not dismiss. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const handlePromote = async (company: string) => {
    setPromoting(company);
    setBanner(null);
    try {
      const res: any = await marketingApi.promoteToPipeline({ company });
      if ((res?.created ?? 0) > 0) {
        setPromoted((prev) => new Set(prev).add(company.toLowerCase()));
        setBanner(`${company} was routed into the pipeline. Assign an owner from Engage → Pipeline.`);
      } else {
        setPromoted((prev) => new Set(prev).add(company.toLowerCase()));
        setBanner(`${company} is already in the pipeline.`);
      }
    } catch {
      setBanner('Could not add to pipeline. Please try again.');
    } finally {
      setPromoting(null);
    }
  };

  const tierStyle = (tier: string) =>
    tier === 'hot'
      ? 'bg-red-100 text-red-700 border-red-200'
      : tier === 'warm'
        ? 'bg-amber-100 text-amber-700 border-amber-200'
        : 'bg-slate-100 text-slate-600 border-slate-200';

  const scoreBar = (score: number) =>
    score >= 60 ? 'bg-red-500' : score >= 30 ? 'bg-amber-500' : 'bg-slate-400';

  const hasPendingRec = (company: string) =>
    recs.some((r) => r.company?.toLowerCase() === company.toLowerCase());

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Account Intent Scores</h3>
          <p className="text-sm text-gray-500">
            Buying signals rolled up per company. Hot accounts trigger a surge alert automatically.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => handleGenerate()}
            disabled={generating}
            className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <Sparkles className={`h-4 w-4 ${generating ? 'animate-pulse' : ''}`} />
            {generating ? 'Drafting…' : 'Draft next best actions'}
          </button>
          <button
            onClick={() => {
              setRefreshing(true);
              void load();
            }}
            disabled={refreshing}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Recompute
          </button>
        </div>
      </div>

      {banner && (
        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-800">
          {banner}
        </div>
      )}

      {recs.length > 0 && (
        <div className="space-y-3">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <Sparkles className="h-4 w-4 text-indigo-500" />
            Recommended actions — review to approve ({recs.length})
          </h4>
          {recs.map((rec) => (
            <div key={rec.id} className="rounded-xl border border-indigo-100 bg-white p-4 shadow-sm">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-900">{rec.company}</span>
                    {typeof rec.score_at_draft === 'number' && (
                      <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
                        score {rec.score_at_draft}
                      </span>
                    )}
                    <span className="text-xs text-gray-400">
                      {rec.model === 'heuristic' ? 'rule-based draft' : 'AI draft'}
                    </span>
                  </div>
                  {rec.rationale && <p className="mt-1 text-sm text-gray-600">{rec.rationale}</p>}
                </div>
                <div className="flex shrink-0 gap-2">
                  <button
                    onClick={() => handleApprove(rec)}
                    disabled={busyId === rec.id}
                    className="inline-flex items-center gap-1 rounded-lg bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" />
                    Approve
                  </button>
                  <button
                    onClick={() => handleReject(rec)}
                    disabled={busyId === rec.id}
                    className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" />
                    Dismiss
                  </button>
                </div>
              </div>
              <ol className="mt-3 space-y-2 border-t border-gray-100 pt-3">
                {(rec.steps || []).map((s, i) => (
                  <li key={i} className="flex gap-3 text-sm">
                    <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-medium text-gray-600">
                      {i + 1}
                    </span>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 text-xs text-gray-500">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 font-medium uppercase">{s.channel}</span>
                        <span>{i === 0 ? 'sends immediately' : `waits ${s.delay_hours}h`}</span>
                      </div>
                      {s.subject && <p className="font-medium text-gray-800">{s.subject}</p>}
                      <p className="whitespace-pre-line text-gray-600 line-clamp-3">{s.content}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </div>
          ))}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <RefreshCw className="h-5 w-5 animate-spin" />
        </div>
      ) : accounts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-200 bg-white py-16 text-center">
          <Building2 className="mx-auto h-8 w-8 text-gray-300" />
          <p className="mt-3 text-sm font-medium text-gray-700">No scored accounts yet</p>
          <p className="mt-1 text-sm text-gray-500">
            Accounts appear here once intent signals accumulate for a company.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="px-4 py-3 text-left font-medium">Company</th>
                <th className="px-4 py-3 text-left font-medium">Score</th>
                <th className="px-4 py-3 text-left font-medium">Tier</th>
                <th className="px-4 py-3 text-left font-medium">Signals</th>
                <th className="px-4 py-3 text-left font-medium">Top signals</th>
                <th className="px-4 py-3 text-right font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {accounts.map((a) => (
                <tr key={a.company} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{a.company}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="h-2 w-24 overflow-hidden rounded-full bg-gray-100">
                        <div
                          className={`h-full ${scoreBar(a.score)}`}
                          style={{ width: `${Math.min(100, a.score)}%` }}
                        />
                      </div>
                      <span className="tabular-nums text-gray-700">{a.score}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${tierStyle(a.tier)}`}
                    >
                      {a.tier}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600">{a.signal_count}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {(a.top_signals || []).slice(0, 4).map((s) => (
                        <span
                          key={s}
                          className="inline-flex rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
                        >
                          {String(s).replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {a.tier === 'hot' && (
                      <div className="flex items-center justify-end gap-2">
                        {hasPendingRec(a.company) ? (
                          <span className="text-xs text-gray-400">draft ready</span>
                        ) : (
                          <button
                            onClick={() => handleGenerate(a.company)}
                            disabled={generating}
                            className="inline-flex items-center gap-1 rounded-lg border border-indigo-200 px-2.5 py-1 text-xs font-medium text-indigo-600 hover:bg-indigo-50 disabled:opacity-50"
                          >
                            <Sparkles className="h-3.5 w-3.5" />
                            Draft
                          </button>
                        )}
                        {promoted.has(a.company.toLowerCase()) ? (
                          <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
                            <Check className="h-3.5 w-3.5" />
                            In pipeline
                          </span>
                        ) : (
                          <button
                            onClick={() => handlePromote(a.company)}
                            disabled={promoting === a.company}
                            className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 px-2.5 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                          >
                            <Target className="h-3.5 w-3.5" />
                            {promoting === a.company ? 'Adding…' : 'To pipeline'}
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface SignalsTabProps {
  signals: BuyingSignal[];
  onRefresh: () => void;
  onCreateMatch: (signal: BuyingSignal) => void;
  onViewSignal: (signal: BuyingSignal) => void;
  onCapture: (signal: BuyingSignal) => void;
  isRefreshing: boolean;
  capabilities: Capability[];
  orgSettings: OrganizationIntentSettings;
}

function SignalsTab({ signals, onRefresh, onCreateMatch, onViewSignal, onCapture, isRefreshing, capabilities, orgSettings }: SignalsTabProps) {
  const { t } = useAppTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStrength, setFilterStrength] = useState<string>('all');
  const [filterSource, setFilterSource] = useState<string>('all');

  const sources = [...new Set(signals.map(s => s.source))];

  // Match signal to Trust Care capabilities based on keywords in category, details, and use cases
  const matchSignalToCapabilities = (signal: BuyingSignal): Capability[] => {
    // In single-vendor mode, only match to organization's capabilities
    if (orgSettings.mode !== 'single-vendor') return [];

    const signalText = `${signal.category} ${signal.details}`.toLowerCase();

    return capabilities.filter(cap => {
      if (cap.status !== 'active') return false;

      // Check if any capability keywords match the signal
      const capKeywords = [
        cap.name.toLowerCase(),
        cap.category.toLowerCase(),
        ...cap.features.map(f => f.toLowerCase()),
        ...cap.useCases.map(u => u.toLowerCase()),
        ...cap.industries.map(i => i.toLowerCase())
      ];

      // Look for matches
      return capKeywords.some(keyword => {
        const words = keyword.split(/\s+/);
        return words.some(word => word.length > 3 && signalText.includes(word));
      });
    }).slice(0, 3); // Limit to top 3 matches
  };

  const filteredSignals = signals.filter(signal => {
    const matchesSearch = signal.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
      signal.category.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStrength = filterStrength === 'all' || signal.strength === filterStrength;
    const matchesSource = filterSource === 'all' || signal.source === filterSource;
    return matchesSearch && matchesStrength && matchesSource;
  });

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{t('platform.intelligence.signals.title', 'Buying Signals')}</h3>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('platform.intelligence.signals.searchPlaceholder', 'Search companies...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>
          <select
            value={filterStrength}
            onChange={(e) => setFilterStrength(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">{t('platform.intelligence.signals.allSignals', 'All Signals')}</option>
            <option value="hot">{t('platform.intelligence.signals.hot', 'Hot')}</option>
            <option value="warm">{t('platform.intelligence.signals.warm', 'Warm')}</option>
            <option value="cold">{t('platform.intelligence.signals.cold', 'Cold')}</option>
          </select>
          <select
            value={filterSource}
            onChange={(e) => setFilterSource(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">{t('platform.intelligence.signals.allSources', 'All Sources')}</option>
            {sources.map(source => (
              <option key={source} value={source}>{source}</option>
            ))}
          </select>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className={`flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 ${isRefreshing ? 'opacity-50' : ''}`}
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            {isRefreshing ? t('platform.intelligence.common.refreshing', 'Refreshing...') : t('platform.intelligence.common.refresh', 'Refresh')}
          </button>
        </div>
      </div>

      <div className="space-y-4">
        {filteredSignals.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('platform.intelligence.signals.noSignals', 'No signals found matching your criteria')}</p>
          </div>
        ) : (
          filteredSignals.map((signal) => {
            const matchedCapabilities = matchSignalToCapabilities(signal);
            return (
              <div key={signal.id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      {getSignalIcon(signal.signalType)}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-semibold text-gray-900">{signal.company}</h4>
                        {getStrengthBadge(signal.strength)}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{signal.category}</p>
                      <p className="text-sm text-gray-700">{signal.details}</p>
                      <div className="flex items-center gap-4 mt-3 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(signal.timestamp).toLocaleString()}
                        </span>
                        <span className="flex items-center gap-1">
                          Source: {signal.source}
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-amber-700 bg-amber-100 text-[10px] font-medium ml-1">
                            Simulated
                          </span>
                        </span>
                      </div>
                      {/* Trust Care Capability Matches - Single Vendor Mode */}
                      {orgSettings.mode === 'single-vendor' && matchedCapabilities.length > 0 && (
                        <div className="mt-4 pt-4 border-t border-gray-100">
                          <div className="flex items-center gap-2 mb-2">
                            <Target className="h-4 w-4 text-purple-600" />
                            <span className="text-sm font-medium text-purple-900">
                              {t('platform.intelligence.signals.capabilityMatches', '{{orgName}} Capability Matches', { orgName: orgSettings.organizationName })}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {matchedCapabilities.map(cap => (
                              <div
                                key={cap.id}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-purple-50 border border-purple-200 rounded-full text-sm"
                              >
                                <CheckCircle className="h-3.5 w-3.5 text-purple-600" />
                                <span className="text-purple-800">{cap.name}</span>
                                <span className="text-purple-500 text-xs">({cap.category})</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => onCapture(signal)}
                      className="px-3 py-1.5 text-sm bg-purple-50 text-purple-700 border border-purple-200 rounded-lg hover:bg-purple-100"
                      title="Persist to the funnel and fire any matching automation"
                    >
                      Capture
                    </button>
                    <button
                      onClick={() => onCreateMatch(signal)}
                      className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      {t('platform.intelligence.signals.createMatch', 'Create Match')}
                    </button>
                    <button
                      onClick={() => onViewSignal(signal)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title={t('platform.intelligence.common.viewDetails', 'View Details')}
                    >
                      <Eye className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

interface MatchesTabProps {
  matches: VendorMatch[];
  signals: BuyingSignal[];
  vendors: Vendor[];
  capabilities: Capability[];
  orgSettings: OrganizationIntentSettings;
  onCreateMatch: () => void;
  onUpdateStatus: (matchId: string, status: VendorMatch['status']) => void;
  onViewMatch: (match: VendorMatch) => void;
}

function MatchesTab({ matches, signals, vendors, capabilities, orgSettings, onCreateMatch, onUpdateStatus, onViewMatch }: MatchesTabProps) {
  const { t } = useAppTranslation();
  // In single-vendor mode, show capability matches instead of vendor matches
  const isSingleVendorMode = orgSettings.mode === 'single-vendor';

  // Match signal to Trust Care capabilities based on keywords (same logic as SignalsTab)
  const matchSignalToCapabilities = (signal: BuyingSignal): { capability: Capability; score: number; reasons: string[] }[] => {
    if (!isSingleVendorMode) return [];

    const signalText = `${signal.category} ${signal.details}`.toLowerCase();
    const matchResults: { capability: Capability; score: number; reasons: string[] }[] = [];

    capabilities.forEach(cap => {
      if (cap.status !== 'active') return;

      const reasons: string[] = [];
      let score = 0;

      // Check name match
      if (signalText.includes(cap.name.toLowerCase())) {
        score += 30;
        reasons.push(`Direct match to ${cap.name}`);
      }

      // Check category match
      if (signalText.includes(cap.category.toLowerCase())) {
        score += 20;
        reasons.push(`Category alignment: ${cap.category}`);
      }

      // Check feature matches
      cap.features.forEach(feature => {
        const featureWords = feature.toLowerCase().split(/\s+/);
        if (featureWords.some(word => word.length > 3 && signalText.includes(word))) {
          score += 15;
          reasons.push(`Feature match: ${feature}`);
        }
      });

      // Check use case matches
      cap.useCases.forEach(useCase => {
        const useCaseWords = useCase.toLowerCase().split(/\s+/);
        if (useCaseWords.some(word => word.length > 3 && signalText.includes(word))) {
          score += 15;
          reasons.push(`Use case alignment: ${useCase}`);
        }
      });

      // Check industry matches
      cap.industries.forEach(industry => {
        if (signalText.includes(industry.toLowerCase())) {
          score += 10;
          reasons.push(`Industry match: ${industry}`);
        }
      });

      if (score > 0 && reasons.length > 0) {
        matchResults.push({
          capability: cap,
          score: Math.min(score, 100),
          reasons: reasons.slice(0, 4) // Limit to 4 reasons
        });
      }
    });

    return matchResults.sort((a, b) => b.score - a.score);
  };

  // Generate capability matches for all hot/warm signals
  const capabilityMatches = isSingleVendorMode
    ? signals
        .filter(signal => signal.strength === 'hot' || signal.strength === 'warm')
        .map(signal => ({
          signal,
          matches: matchSignalToCapabilities(signal)
        }))
        .filter(item => item.matches.length > 0)
    : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">
          {isSingleVendorMode ? t('platform.intelligence.matches.capabilityMatchesTitle', '{{orgName}} Capability Matches', { orgName: orgSettings.organizationName }) : t('platform.intelligence.matches.vendorMatchesTitle', 'Vendor Matches')}
        </h3>
        <button
          onClick={onCreateMatch}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          {t('platform.intelligence.matches.manualMatch', 'Manual Match')}
        </button>
      </div>

      {/* Single-Vendor Mode Banner */}
      {isSingleVendorMode && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Shield className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h4 className="font-medium text-purple-900">{t('platform.intelligence.matches.singleVendorModeTitle', 'Single-Vendor Mode: {{orgName}}', { orgName: orgSettings.organizationName })}</h4>
              <p className="text-sm text-purple-700">
                {t('platform.intelligence.matches.singleVendorModeDescription', "Showing matches between buying signals and {{orgName}}'s verified capabilities only. Switch to Systems Integrator mode in Settings to match with external vendors.", { orgName: orgSettings.organizationName })}
              </p>
            </div>
          </div>
        </div>
      )}

      {isSingleVendorMode ? (
        // Capability Matches View (Single-Vendor Mode)
        capabilityMatches.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Target className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('platform.intelligence.matches.noCapabilityMatches', 'No capability matches found.')}</p>
            <p className="text-sm text-gray-400 mt-1">{t('platform.intelligence.matches.autoMatchDescription', 'Hot and warm signals will be automatically matched to {{orgName}} capabilities.', { orgName: orgSettings.organizationName })}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {capabilityMatches.map(({ signal, matches: capMatches }) => (
              <div key={signal.id} className="bg-white rounded-lg border border-gray-200 p-6">
                {/* Signal Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{signal.company}</h4>
                        <p className="text-sm text-gray-500">{signal.category}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400" />
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Star className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{orgSettings.organizationName}</h4>
                        <p className="text-sm text-gray-500">{capMatches.length} Capability Match{capMatches.length !== 1 ? 'es' : ''}</p>
                      </div>
                    </div>
                  </div>
                  {getStrengthBadge(signal.strength)}
                </div>

                {/* Matched Capabilities */}
                <div className="space-y-3">
                  {capMatches.map(({ capability, score, reasons }) => (
                    <div key={capability.id} className="bg-purple-50 rounded-lg p-4 border border-purple-100">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="p-1.5 bg-purple-100 rounded">
                            <CheckCircle className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <h5 className="font-medium text-purple-900">{capability.name}</h5>
                            <p className="text-sm text-purple-600">{capability.category}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-purple-600">{score}%</div>
                          <div className="text-xs text-purple-500">{t('platform.intelligence.matches.matchScore', 'Match Score')}</div>
                        </div>
                      </div>
                      <div className="pl-9">
                        <h6 className="text-xs font-medium text-purple-700 uppercase mb-1.5">{t('platform.intelligence.matches.matchReasons', 'Match Reasons')}:</h6>
                        <ul className="space-y-1">
                          {reasons.map((reason, i) => (
                            <li key={i} className="flex items-center gap-2 text-sm text-purple-800">
                              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full"></div>
                              {reason}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Signal Details */}
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-sm text-gray-600">{signal.details}</p>
                  <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      {new Date(signal.timestamp).toLocaleString()}
                    </span>
                    <span>Source: {signal.source}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      ) : (
        // Vendor Matches View (Systems Integrator Mode)
        matches.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <Target className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">{t('platform.intelligence.matches.noVendorMatches', 'No matches yet. Create a match to get started.')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {matches.map((match) => (
              <div key={match.id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Building2 className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{match.signal.company}</h4>
                        <p className="text-sm text-gray-500">{match.signal.category}</p>
                      </div>
                    </div>
                    <ArrowRight className="h-5 w-5 text-gray-400" />
                    <div className="flex items-center gap-2">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Target className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">{match.vendor.name}</h4>
                        <p className="text-sm text-gray-500">{match.vendor.category}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="text-2xl font-bold text-green-600">{match.matchScore}%</div>
                      <div className="text-xs text-gray-500">Match Score</div>
                    </div>
                    {getStatusBadge(match.status)}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <h5 className="text-sm font-medium text-gray-700 mb-2">{t('platform.intelligence.matches.matchReasons', 'Match Reasons')}:</h5>
                  <ul className="space-y-1">
                    {match.matchReasons.map((reason, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        {reason}
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="flex items-center justify-between">
                  <div className="text-sm text-gray-500">
                    {match.assignedTo && <span>Assigned to: {match.assignedTo}</span>}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={match.status}
                      onChange={(e) => onUpdateStatus(match.id, e.target.value as VendorMatch['status'])}
                      className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="pending">Pending</option>
                      <option value="contacted">Contacted</option>
                      <option value="qualified">Qualified</option>
                      <option value="disqualified">Disqualified</option>
                    </select>
                    <button
                      onClick={() => onViewMatch(match)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                    >
                      <Eye className="h-4 w-4 text-gray-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

interface VendorsTabProps {
  vendors: Vendor[];
  onAddVendor: () => void;
  onEditVendor: (vendor: Vendor) => void;
  onDeleteVendor: (vendor: Vendor) => void;
}

function VendorsTab({ vendors, onAddVendor, onEditVendor, onDeleteVendor }: VendorsTabProps) {
  const { t } = useAppTranslation();
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{t('platform.intelligence.vendors.title', 'Vendor Capabilities')}</h3>
        <button
          onClick={onAddVendor}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          <Plus className="h-4 w-4" />
          {t('platform.intelligence.vendors.addVendor', 'Add Vendor')}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {vendors.map((vendor) => (
          <div key={vendor.id} className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h4 className="font-semibold text-gray-900">{vendor.name}</h4>
                <p className="text-sm text-gray-500">{vendor.category}</p>
              </div>
              <div className="flex items-center gap-1">
                <span className="text-yellow-500">★</span>
                <span className="font-medium">{vendor.rating}</span>
              </div>
            </div>

            <div className="mb-4">
              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">{t('platform.intelligence.vendors.capabilities', 'Capabilities')}</h5>
              <div className="flex flex-wrap gap-2">
                {vendor.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="text-sm">
                <span className="text-gray-500">{t('platform.intelligence.vendors.activeDeals', 'Active Deals')}: </span>
                <span className="font-medium text-gray-900">{vendor.activeDeals}</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEditVendor(vendor)}
                  className="p-1.5 hover:bg-gray-100 rounded"
                  title={t('platform.intelligence.vendors.editVendor', 'Edit Vendor')}
                >
                  <Edit className="h-4 w-4 text-gray-500" />
                </button>
                <button
                  onClick={() => onDeleteVendor(vendor)}
                  className="p-1.5 hover:bg-gray-100 rounded"
                  title={t('platform.intelligence.vendors.deleteVendor', 'Delete Vendor')}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// CAPABILITIES TAB (Single-Vendor Mode)
// ============================================================================

interface CapabilitiesTabProps {
  capabilities: Capability[];
  organizationName: string;
  onAddCapability: () => void;
  onEditCapability: (capability: Capability) => void;
  onDeleteCapability: (capability: Capability) => void;
}

function CapabilitiesTab({
  capabilities,
  organizationName,
  onAddCapability,
  onEditCapability,
  onDeleteCapability
}: CapabilitiesTabProps) {
  const { t } = useAppTranslation();
  const [filterStatus, setFilterStatus] = useState<Capability['status'] | 'all'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');

  const categories = Array.from(new Set(capabilities.map(c => c.category)));

  const filteredCapabilities = capabilities.filter(cap => {
    if (filterStatus !== 'all' && cap.status !== filterStatus) return false;
    if (filterCategory !== 'all' && cap.category !== filterCategory) return false;
    return true;
  });

  const totalMatches = capabilities.reduce((sum, cap) => sum + cap.signalMatches, 0);

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('platform.intelligence.capabilities.title', '{{orgName}} Capabilities', { orgName: organizationName })}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {t('platform.intelligence.capabilities.subtitle', 'Define your product and service capabilities to match with buying signals')}
          </p>
        </div>
        <button
          onClick={onAddCapability}
          className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
        >
          <Plus className="h-4 w-4" />
          {t('platform.intelligence.capabilities.addCapability', 'Add Capability')}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.capabilities.totalCapabilities', 'Total Capabilities')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{capabilities.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.capabilities.active', 'Active')}</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {capabilities.filter(c => c.status === 'active').length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.capabilities.signalMatches', 'Signal Matches')}</p>
          <p className="text-2xl font-bold text-purple-600 mt-1">{totalMatches}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.capabilities.categories', 'Categories')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{categories.length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-4 mb-6">
        <div className="flex items-center gap-2">
          <Filter className="h-4 w-4 text-gray-500" />
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value as Capability['status'] | 'all')}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="all">{t('platform.intelligence.capabilities.allStatus', 'All Status')}</option>
            <option value="active">{t('platform.intelligence.capabilities.statusActive', 'Active')}</option>
            <option value="draft">{t('platform.intelligence.capabilities.statusDraft', 'Draft')}</option>
            <option value="deprecated">{t('platform.intelligence.capabilities.statusDeprecated', 'Deprecated')}</option>
          </select>
        </div>
        <select
          value={filterCategory}
          onChange={(e) => setFilterCategory(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="all">{t('platform.intelligence.capabilities.allCategories', 'All Categories')}</option>
          {categories.map(cat => (
            <option key={cat} value={cat}>{cat}</option>
          ))}
        </select>
      </div>

      {/* Capabilities Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {filteredCapabilities.map((capability) => (
          <div key={capability.id} className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <h4 className="font-semibold text-gray-900">{capability.name}</h4>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    capability.status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : capability.status === 'draft'
                        ? 'bg-yellow-100 text-yellow-700'
                        : 'bg-gray-100 text-gray-700'
                  }`}>
                    {capability.status}
                  </span>
                </div>
                <p className="text-sm text-gray-500">{capability.category}</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onEditCapability(capability)}
                  className="p-1.5 hover:bg-gray-100 rounded"
                  title="Edit Capability"
                >
                  <Edit className="h-4 w-4 text-gray-500" />
                </button>
                <button
                  onClick={() => onDeleteCapability(capability)}
                  className="p-1.5 hover:bg-gray-100 rounded"
                  title="Delete Capability"
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </button>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-4">{capability.description}</p>

            {/* Features */}
            <div className="mb-4">
              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">{t('platform.intelligence.capabilities.keyFeaturesLabel', 'Key Features')}</h5>
              <div className="flex flex-wrap gap-2">
                {capability.features.slice(0, 4).map((feature) => (
                  <span
                    key={feature}
                    className="px-2 py-1 bg-purple-50 text-purple-700 text-xs rounded-full"
                  >
                    {feature}
                  </span>
                ))}
                {capability.features.length > 4 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{capability.features.length - 4} more
                  </span>
                )}
              </div>
            </div>

            {/* Use Cases */}
            <div className="mb-4">
              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">{t('platform.intelligence.capabilities.useCasesLabel', 'Use Cases')}</h5>
              <div className="flex flex-wrap gap-2">
                {capability.useCases.slice(0, 3).map((useCase) => (
                  <span
                    key={useCase}
                    className="px-2 py-1 bg-blue-50 text-blue-700 text-xs rounded-full"
                  >
                    {useCase}
                  </span>
                ))}
                {capability.useCases.length > 3 && (
                  <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                    +{capability.useCases.length - 3} more
                  </span>
                )}
              </div>
            </div>

            {/* Industries */}
            <div className="mb-4">
              <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">{t('platform.intelligence.capabilities.targetIndustriesLabel', 'Target Industries')}</h5>
              <div className="flex flex-wrap gap-2">
                {capability.industries.map((industry) => (
                  <span
                    key={industry}
                    className="px-2 py-1 bg-green-50 text-green-700 text-xs rounded-full"
                  >
                    {industry}
                  </span>
                ))}
              </div>
            </div>

            {/* Footer Stats */}
            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <div className="flex items-center gap-4">
                <div className="text-sm">
                  <span className="text-gray-500">{t('platform.intelligence.capabilities.signalMatchesLabel', 'Signal Matches')}: </span>
                  <span className="font-medium text-purple-600">{capability.signalMatches}</span>
                </div>
              </div>
              <button
                className="text-sm text-purple-600 hover:text-purple-700 flex items-center gap-1"
              >
                {t('platform.intelligence.capabilities.viewMatches', 'View Matches')}
                <ArrowRight className="h-3 w-3" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {filteredCapabilities.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Layers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('platform.intelligence.capabilities.noCapabilities', 'No capabilities found')}</h3>
          <p className="text-gray-500 mb-4">
            {filterStatus !== 'all' || filterCategory !== 'all'
              ? t('platform.intelligence.capabilities.adjustFilters', 'Try adjusting your filters')
              : t('platform.intelligence.capabilities.addFirstCapability', 'Add your first capability to start matching with buying signals')}
          </p>
          {filterStatus === 'all' && filterCategory === 'all' && (
            <button
              onClick={onAddCapability}
              className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="h-4 w-4" />
              {t('platform.intelligence.capabilities.addCapability', 'Add Capability')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function AnalyticsTab() {
  const { t } = useAppTranslation();
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900">{t('platform.intelligence.analytics.title', 'Intent Analytics')}</h3>
        <select className="px-4 py-2 border border-gray-300 rounded-lg">
          <option>{t('platform.intelligence.analytics.last30Days', 'Last 30 days')}</option>
          <option>{t('platform.intelligence.analytics.last7Days', 'Last 7 days')}</option>
          <option>{t('platform.intelligence.analytics.last90Days', 'Last 90 days')}</option>
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.analytics.totalSignals', 'Total Signals')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">1,248</p>
          <p className="text-sm text-green-600 mt-1">{t('platform.intelligence.analytics.percentFromLastPeriod', '+23% from last period')}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.analytics.matchesCreated', 'Matches Created')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">342</p>
          <p className="text-sm text-green-600 mt-1">+15% {t('platform.intelligence.analytics.fromLastPeriod', 'from last period')}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.analytics.matchRate', 'Match Rate')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">27.4%</p>
          <p className="text-sm text-green-600 mt-1">+2.1% {t('platform.intelligence.analytics.fromLastPeriod', 'from last period')}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.analytics.conversionRate', 'Conversion Rate')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">12.8%</p>
          <p className="text-sm text-red-600 mt-1">-0.5% {t('platform.intelligence.analytics.fromLastPeriod', 'from last period')}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">{t('platform.intelligence.analytics.signalTypesDistribution', 'Signal Types Distribution')}</h4>
          <div className="space-y-3">
            {[
              { type: t('platform.intelligence.analytics.pricingPageVisits', 'Pricing Page Visits'), count: 456, pct: 36.5 },
              { type: t('platform.intelligence.analytics.contentDownloads', 'Content Downloads'), count: 312, pct: 25.0 },
              { type: t('platform.intelligence.analytics.demoRequests', 'Demo Requests'), count: 234, pct: 18.8 },
              { type: t('platform.intelligence.analytics.competitorResearch', 'Competitor Research'), count: 156, pct: 12.5 },
              { type: t('platform.intelligence.analytics.jobPostings', 'Job Postings'), count: 90, pct: 7.2 },
            ].map((item) => (
              <div key={item.type} className="flex items-center gap-4">
                <div className="w-32 text-sm text-gray-700">{item.type}</div>
                <div className="flex-1">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-blue-600 rounded-full"
                      style={{ width: `${item.pct}%` }}
                    />
                  </div>
                </div>
                <div className="w-16 text-sm text-gray-600 text-right">{item.count}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h4 className="font-medium text-gray-900 mb-4">{t('platform.intelligence.analytics.topMatchedIndustries', 'Top Matched Industries')}</h4>
          <div className="space-y-3">
            {[
              { industry: t('platform.intelligence.analytics.technology', 'Technology'), matches: 89, conversion: '18.5%' },
              { industry: t('platform.intelligence.analytics.healthcare', 'Healthcare'), matches: 67, conversion: '15.2%' },
              { industry: t('platform.intelligence.analytics.financialServices', 'Financial Services'), matches: 54, conversion: '21.3%' },
              { industry: t('platform.intelligence.analytics.retail', 'Retail'), matches: 45, conversion: '12.1%' },
              { industry: t('platform.intelligence.analytics.manufacturing', 'Manufacturing'), matches: 32, conversion: '9.8%' },
            ].map((item) => (
              <div key={item.industry} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <div>
                  <div className="font-medium text-gray-900">{item.industry}</div>
                  <div className="text-sm text-gray-500">{item.matches} {t('platform.intelligence.analytics.matches', 'matches')}</div>
                </div>
                <div className="text-right">
                  <div className="font-medium text-green-600">{item.conversion}</div>
                  <div className="text-xs text-gray-500">{t('platform.intelligence.analytics.conversion', 'conversion')}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// OPPORTUNITIES TAB (Program Intelligence - Outbound)
// ============================================================================

// Opportunity types for market intelligence
type OpportunityType = 'healthcare_program' | 'rfp' | 'procurement' | 'government_contract' | 'partnership';
type OpportunityStatus = 'discovered' | 'evaluating' | 'pursuing' | 'applied' | 'won' | 'lost' | 'expired';

interface MarketOpportunity {
  id: string;
  name: string;
  organization: string;
  type: OpportunityType;
  description: string;
  status: OpportunityStatus;
  industry: string;
  geography: string[];
  budget?: { min: number; max: number };
  deadline?: string;
  discoveredAt: string;
  matchScore: number;
  requiredCapabilities: string[];
  source: string;
  // Rich Program Intelligence fields
  sponsorType?: string;
  targetEnrollment?: number;
  estimatedPerMemberValue?: number;
  totalInterestedCbos?: number;
  executionReadinessScore?: number;
  serviceCategories?: string[];
  sdohFocusAreas?: string[];
  coveredServices?: string[];
  requiredCertifications?: string[];
  reimbursementModel?: string;
  baseReimbursementRate?: number;
  totalProgramBudget?: number;
  programStartDate?: string;
  programEndDate?: string;
  eligibilityCriteria?: {
    insurance_types?: string[];
    age_min?: number;
    enrollment_required?: boolean;
    attribution_method?: string;
  };
  qualityPerformance?: {
    quality_score?: number;
    per_capita_expenditure?: number;
  };
  financialPerformance?: {
    savings_rate?: number;
    generated_savings_losses?: number;
  };
  numberOfProvidersNeeded?: number;
  prioritySpecialtiesNeeded?: string[];
  enrollmentTrendDirection?: 'growing' | 'declining' | 'stable';
  enrollmentGrowthRate12mo?: number;
  riskArrangement?: string;
  sourceUrl?: string;
  confidenceScore?: number; // Raw confidence score from API
}


function OpportunitiesTab({ onNavigate }: { onNavigate?: (tab: string) => void } = {}) {
  const { t } = useAppTranslation();
  const [opportunities, setOpportunities] = useState<MarketOpportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<OpportunityType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<OpportunityStatus | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  // Expanded cards state
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  // Favorited programs state
  const [favoritedPrograms, setFavoritedPrograms] = useState<Set<string>>(new Set());
  // Interest modal state
  const [selectedOpportunity, setSelectedOpportunity] = useState<MarketOpportunity | null>(null);
  const [showInterestModal, setShowInterestModal] = useState(false);
  const [interestLevel, setInterestLevel] = useState<string>('HIGH');
  const [interestType, setInterestType] = useState<string>('PARTICIPATE');
  const [declaredCapacity, setDeclaredCapacity] = useState<number>(0);
  const [interestNotes, setInterestNotes] = useState('');
  const [submittingInterest, setSubmittingInterest] = useState(false);
  // Pipeline integration state
  const [showPipelineModal, setShowPipelineModal] = useState(false);
  const [pipelineStage, setPipelineStage] = useState<string>('prospecting');
  // Owner is chosen from the real staff roster (fetched below). Never seed a
  // hardcoded person name — a placeholder owner gets persisted verbatim to
  // POST /api/platform/sales/opportunities and poisons the pipeline.
  const [pipelineOwner, setPipelineOwner] = useState<string>('');
  const [staffUsers, setStaffUsers] = useState<Array<{ id: number | string; name: string }>>([]);
  const [addingToPipeline, setAddingToPipeline] = useState(false);

  // Load the assignable staff roster for the owner dropdown.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await apiClient.get('/api/users/all-staff');
        const roster = (resp.data || [])
          .filter((u: any) => u?.name)
          .map((u: any) => ({ id: u.id, name: u.name }));
        if (!cancelled) setStaffUsers(roster);
      } catch (err) {
        // Leave the roster empty — the dropdown renders a disabled
        // "No users available" option rather than fake names.
        if (!cancelled) setStaffUsers([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Transform API response to MarketOpportunity
  const transformProgram = (p: any): MarketOpportunity => {
    // Determine opportunity type based on program data
    let type: OpportunityType = 'healthcare_program';
    if (p.rfp_status === 'OPEN') type = 'rfp';
    else if (p.discovered_sponsor_type === 'STATE_AUTHORITY' || p.discovered_sponsor_type === 'FEDERAL_AGENCY') {
      type = 'government_contract';
    } else if (p.program_type?.includes('Partnership')) type = 'partnership';

    // Determine status based on intelligence_status
    let status: OpportunityStatus = 'discovered';
    if (p.intelligence_status === 'EVALUATING') status = 'evaluating';
    else if (p.intelligence_status === 'PURSUING') status = 'pursuing';
    else if (p.intelligence_status === 'APPLIED') status = 'applied';
    else if (p.intelligence_status === 'WON') status = 'won';
    else if (p.intelligence_status === 'LOST') status = 'lost';
    else if (p.application_deadline && new Date(p.application_deadline) < new Date()) {
      status = 'expired';
    }

    // Calculate match score - use confidence_score if available, otherwise calculate based on data completeness
    // Do NOT use random values - use actual data indicators
    // IMPORTANT: API returns confidence_score as decimal (0.0-1.0), need to convert to percentage (0-100)
    let matchScore = 0;
    if (p.confidence_score !== null && p.confidence_score !== undefined) {
      // Check if score is already in percentage format (> 1) or decimal format (0-1)
      // If decimal (e.g., 0.85), multiply by 100 to get percentage (85%)
      matchScore = p.confidence_score <= 1 ? Math.round(p.confidence_score * 100) : p.confidence_score;
    } else if (p.execution_readiness_score) {
      // Use execution readiness as primary fallback - it's already in percentage format
      matchScore = p.execution_readiness_score;
    } else {
      // Calculate based on data completeness and relevance indicators
      let score = 50; // Base score - start at 50% for any discovered program
      // Add points for rich data availability
      if (p.service_categories?.length > 0) score += 10;
      if (p.target_enrollment && p.target_enrollment > 0) score += 8;
      if (p.estimated_per_member_value && p.estimated_per_member_value > 0) score += 7;
      if (p.application_deadline) score += 5;
      if (p.covered_services?.length > 0) score += 5;
      if (p.total_interested_cbos && p.total_interested_cbos > 0) score += 5;
      if (p.sdoh_focus_areas?.length > 0) score += 5;
      if (p.eligibility_criteria) score += 5;
      matchScore = Math.min(100, score);
    }

    return {
      id: String(p.id),
      name: p.program_name || 'Unnamed Program',
      organization: p.discovered_sponsor_name || 'Unknown Sponsor',
      type,
      description: p.description || '',
      status,
      industry: p.discovered_sponsor_type === 'HEALTH_PLAN' ? 'Healthcare' :
                p.discovered_sponsor_type === 'STATE_AUTHORITY' ? 'Government' : 'Healthcare',
      geography: p.service_states || [p.sponsor_headquarters_state].filter(Boolean),
      budget: p.total_program_budget ? { min: p.total_program_budget * 0.8, max: p.total_program_budget } : undefined,
      deadline: p.application_deadline || undefined,
      discoveredAt: p.discovered_at || new Date().toISOString(),
      matchScore,
      requiredCapabilities: [
        ...(p.service_categories || []),
        ...(p.sdoh_focus_areas || []),
        ...(p.required_certifications || [])
      ].slice(0, 5),
      source: p.data_source || 'Program Intelligence',
      // Rich Program Intelligence fields
      sponsorType: p.discovered_sponsor_type,
      targetEnrollment: p.target_enrollment,
      estimatedPerMemberValue: p.estimated_per_member_value,
      totalInterestedCbos: p.total_interested_cbos || 0,
      executionReadinessScore: p.execution_readiness_score,
      serviceCategories: p.service_categories,
      sdohFocusAreas: p.sdoh_focus_areas,
      coveredServices: p.covered_services,
      requiredCertifications: p.required_certifications,
      reimbursementModel: p.reimbursement_model,
      baseReimbursementRate: p.base_reimbursement_rate,
      totalProgramBudget: p.total_program_budget,
      programStartDate: p.program_start_date,
      programEndDate: p.program_end_date,
      eligibilityCriteria: p.eligibility_criteria,
      qualityPerformance: p.quality_performance,
      financialPerformance: p.financial_performance,
      numberOfProvidersNeeded: p.number_of_providers_needed,
      prioritySpecialtiesNeeded: p.priority_specialties_needed,
      enrollmentTrendDirection: p.enrollment_trend_direction,
      enrollmentGrowthRate12mo: p.enrollment_growth_rate_12mo,
      riskArrangement: p.risk_arrangement,
      sourceUrl: p.source_url,
      confidenceScore: p.confidence_score,
    };
  };

  // Fetch real program intelligence data
  useEffect(() => {
    const fetchOpportunities = async () => {
      try {
        setLoading(true);
        setError(null);

        const cboId = localStorage.getItem('active_cbo_id') || '1';
        const token = localStorage.getItem('auth_token');
        const url = getWeaveinApiUrl('/api/program-intelligence?limit=100');

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId
          }
        });

        if (!response.ok) {
          // No mock fallback — surface the error and show the empty-state.
          console.warn('Failed to fetch program intelligence');
          setError('Could not load opportunities.');
          setOpportunities([]);
          return;
        }

        const programs = await response.json();
        const transformed: MarketOpportunity[] = (programs || []).map(transformProgram);
        setOpportunities(transformed);
      } catch (err) {
        console.error('Error fetching opportunities:', err);
        setError('Could not load opportunities.');
        setOpportunities([]);
      } finally {
        setLoading(false);
      }
    };

    fetchOpportunities();
  }, []);

  const filteredOpportunities = opportunities.filter(opp => {
    if (filterType !== 'all' && opp.type !== filterType) return false;
    if (filterStatus !== 'all' && opp.status !== filterStatus) return false;
    if (searchQuery && !opp.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !opp.organization.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  const getTypeLabel = (type: OpportunityType): string => {
    const labels: Record<OpportunityType, string> = {
      healthcare_program: 'Healthcare Program',
      rfp: 'RFP',
      procurement: 'Procurement',
      government_contract: 'Government Contract',
      partnership: 'Partnership',
    };
    return labels[type];
  };

  const getStatusColor = (status: OpportunityStatus): string => {
    const colors: Record<OpportunityStatus, string> = {
      discovered: 'bg-blue-100 text-blue-800',
      evaluating: 'bg-yellow-100 text-yellow-800',
      pursuing: 'bg-purple-100 text-purple-800',
      applied: 'bg-indigo-100 text-indigo-800',
      won: 'bg-green-100 text-green-800',
      lost: 'bg-red-100 text-red-800',
      expired: 'bg-gray-100 text-gray-800',
    };
    return colors[status];
  };

  const formatBudget = (budget?: { min: number; max: number }): string => {
    if (!budget) return 'Not specified';
    const format = (n: number) => {
      if (n >= 1000000) return `$${(n / 1000000).toFixed(1)}M`;
      if (n >= 1000) return `$${(n / 1000).toFixed(0)}K`;
      return `$${n}`;
    };
    return `${format(budget.min)} - ${format(budget.max)}`;
  };

  const formatCurrency = (amount?: number): string => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysUntilDeadline = (deadline?: string): number | null => {
    if (!deadline) return null;
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate.getTime() - today.getTime();
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getSponsorTypeBadgeColor = (type?: string): string => {
    const colors: Record<string, string> = {
      'HEALTH_PLAN': 'bg-blue-100 text-blue-800',
      'MANAGED_CARE': 'bg-purple-100 text-purple-800',
      'STATE_AUTHORITY': 'bg-green-100 text-green-800',
      'STATE_ASSOCIATION': 'bg-teal-100 text-teal-800',
      'FEDERAL_AGENCY': 'bg-red-100 text-red-800',
      'ACO': 'bg-orange-100 text-orange-800',
      'HOSPITAL_SYSTEM': 'bg-indigo-100 text-indigo-800'
    };
    return colors[type || ''] || 'bg-gray-100 text-gray-800';
  };

  const toggleCardExpansion = (oppId: string) => {
    setExpandedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(oppId)) {
        newSet.delete(oppId);
      } else {
        newSet.add(oppId);
      }
      return newSet;
    });
  };

  // Toggle favorite/flag for an opportunity
  const toggleFavorite = async (opp: MarketOpportunity) => {
    const isFavorited = favoritedPrograms.has(opp.id);
    const cboId = localStorage.getItem('active_cbo_id') || '1';
    const token = localStorage.getItem('auth_token');

    try {
      const url = getWeaveinApiUrl(`/api/program-intelligence/${opp.id}/favorite`);
      const response = await fetch(url, {
        method: isFavorited ? 'DELETE' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CBO-ID': cboId
        }
      });

      if (response.ok) {
        setFavoritedPrograms(prev => {
          const newSet = new Set(prev);
          if (isFavorited) {
            newSet.delete(opp.id);
          } else {
            newSet.add(opp.id);
          }
          return newSet;
        });
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      // Still update local state for better UX
      setFavoritedPrograms(prev => {
        const newSet = new Set(prev);
        if (isFavorited) {
          newSet.delete(opp.id);
        } else {
          newSet.add(opp.id);
        }
        return newSet;
      });
    }
  };

  // Open express interest modal
  const handleExpressInterest = (opp: MarketOpportunity) => {
    setSelectedOpportunity(opp);
    setShowInterestModal(true);
    setInterestLevel('HIGH');
    setInterestType('PARTICIPATE');
    setDeclaredCapacity(0);
    setInterestNotes('');
  };

  // Submit interest to API
  const submitInterest = async () => {
    if (!selectedOpportunity) return;

    const cboId = localStorage.getItem('active_cbo_id') || '1';
    const token = localStorage.getItem('auth_token');

    try {
      setSubmittingInterest(true);
      const url = getWeaveinApiUrl(`/api/program-intelligence/${selectedOpportunity.id}/express-interest`);

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'X-CBO-ID': cboId
        },
        body: JSON.stringify({
          interest_level: interestLevel,
          interest_type: interestType,
          declared_capacity: declaredCapacity || null,
          interest_notes: interestNotes || null
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to express interest');
      }

      setShowInterestModal(false);
      setSelectedOpportunity(null);
      // Refresh to get updated counts
      handleRefresh();
      alert('Interest expressed successfully! The sponsor will be notified.');
    } catch (err) {
      console.error('Error expressing interest:', err);
      alert(err instanceof Error ? err.message : 'Failed to express interest');
    } finally {
      setSubmittingInterest(false);
    }
  };

  // Handler to refresh opportunities
  const handleRefresh = () => {
    setLoading(true);
    const fetchOpportunities = async () => {
      try {
        const cboId = localStorage.getItem('active_cbo_id') || '1';
        const token = localStorage.getItem('auth_token');
        const url = getWeaveinApiUrl('/api/program-intelligence?limit=100');

        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'X-CBO-ID': cboId
          }
        });

        if (response.ok) {
          const programs = await response.json();
          const transformed: MarketOpportunity[] = (programs || []).map(transformProgram);
          if (transformed.length > 0) setOpportunities(transformed);
        }
      } catch (err) {
        console.error('Error refreshing:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOpportunities();
  };

  // Update opportunity status locally
  const handleUpdateStatus = (oppId: string, newStatus: OpportunityStatus) => {
    setOpportunities(prev =>
      prev.map(opp =>
        opp.id === oppId ? { ...opp, status: newStatus } : opp
      )
    );
    // TODO: API call to persist status change
  };

  // Open pipeline modal
  const handleAddToPipeline = (opp: MarketOpportunity) => {
    setSelectedOpportunity(opp);
    setShowPipelineModal(true);
    setPipelineStage('prospecting');
    setPipelineOwner(staffUsers[0]?.name || '');
  };

  // Submit to Sales Pipeline
  const submitToPipeline = async () => {
    if (!selectedOpportunity) return;

    try {
      setAddingToPipeline(true);

      // Calculate estimated deal value from budget
      const estimatedValue = selectedOpportunity.budget
        ? Math.round((selectedOpportunity.budget.min + selectedOpportunity.budget.max) / 2)
        : 0;

      // POST to the real sales pipeline API. The backend handler
      // (POST /api/platform/sales/opportunities) scopes the new row to the
      // calling user's cbo_id, so single-vendor Triveny tenants land the
      // opportunity in their own pipeline.
      const probability =
        pipelineStage === 'prospecting' ? 10 :
        pipelineStage === 'qualification' ? 25 :
        pipelineStage === 'proposal' ? 50 :
        pipelineStage === 'negotiation' ? 75 : 40;

      const newOppPayload = {
        name: selectedOpportunity.name,
        company: selectedOpportunity.organization,
        amount: estimatedValue,
        stage: pipelineStage,
        probability,
        close_date: selectedOpportunity.deadline || getRelativeDate(90),
        owner_name: pipelineOwner,
        source: 'intelligence',
        notes: `Created from Intelligence signal · type=${selectedOpportunity.type} · opp_id=${selectedOpportunity.id}`,
      } as any;

      let apiSucceeded = false;
      try {
        const resp = await salesApi.createOpportunity(newOppPayload);
        apiSucceeded = !!resp?.success;
      } catch (apiErr) {
        // Fall through to localStorage stash below — the modal still closes
        // and the opp moves to 'pursuing' so the demo flow isn't blocked.
        console.warn('salesApi.createOpportunity failed, falling back to localStorage:', apiErr);
      }

      if (!apiSucceeded) {
        const pipelineItem = {
          id: `pipeline-${Date.now()}`,
          ...newOppPayload,
          lastActivity: new Date().toISOString().split('T')[0],
          sourceOpportunityId: selectedOpportunity.id,
          sourceType: selectedOpportunity.type,
        };
        const existingPipeline = JSON.parse(localStorage.getItem('sales_pipeline_items') || '[]');
        existingPipeline.push(pipelineItem);
        localStorage.setItem('sales_pipeline_items', JSON.stringify(existingPipeline));
      }

      // Update opportunity status to 'pursuing'
      handleUpdateStatus(selectedOpportunity.id, 'pursuing');

      setShowPipelineModal(false);
      setSelectedOpportunity(null);

      // On success: navigate the user to the Sales Pipeline page so the new
      // kanban card is visible immediately. SalesPipelinePage refetches on
      // mount, so the new row appears without any extra wiring. Falls back
      // to an alert if no navigation handler was passed (e.g. the page was
      // mounted outside the App shell, like in storybook).
      if (apiSucceeded && onNavigate) {
        onNavigate('platform-sales');
      } else {
        alert(
          apiSucceeded
            ? `Added to Sales Pipeline: ${selectedOpportunity.name}\n\nOpen the Sales Pipeline page to see it on the kanban.`
            : `Saved locally: ${selectedOpportunity.name}\n\n(API unavailable — will sync once the sales pipeline service is reachable.)`,
        );
      }
    } catch (err) {
      console.error('Error adding to pipeline:', err);
      alert('Failed to add to pipeline');
    } finally {
      setAddingToPipeline(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        <span className="ml-3 text-gray-600">{t('platform.intelligence.opportunities.loading', 'Loading opportunities from Program Intelligence...')}</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{t('platform.intelligence.opportunities.title', 'Market Opportunities')}</h3>
          <p className="text-sm text-gray-500 mt-1">
            {t('platform.intelligence.opportunities.subtitle', 'Discovered programs, RFPs, and partnership opportunities matching your capabilities')}
            <span className="ml-2 text-xs text-blue-600">({opportunities.length} {t('platform.intelligence.opportunities.total', 'total')})</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            {t('platform.intelligence.opportunities.refreshOpportunities', 'Refresh Opportunities')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder={t('platform.intelligence.opportunities.searchPlaceholder', 'Search opportunities...')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value as OpportunityType | 'all')}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="all">{t('platform.intelligence.opportunities.allTypes', 'All Types')}</option>
          <option value="healthcare_program">{t('platform.intelligence.opportunities.healthcarePrograms', 'Healthcare Programs')}</option>
          <option value="rfp">{t('platform.intelligence.opportunities.rfps', 'RFPs')}</option>
          <option value="procurement">{t('platform.intelligence.opportunities.procurement', 'Procurement')}</option>
          <option value="government_contract">{t('platform.intelligence.opportunities.governmentContracts', 'Government Contracts')}</option>
          <option value="partnership">{t('platform.intelligence.opportunities.partnerships', 'Partnerships')}</option>
        </select>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value as OpportunityStatus | 'all')}
          className="px-4 py-2 border border-gray-300 rounded-lg"
        >
          <option value="all">{t('platform.intelligence.opportunities.allStatuses', 'All Statuses')}</option>
          <option value="discovered">{t('platform.intelligence.opportunities.statusDiscovered', 'Discovered')}</option>
          <option value="evaluating">{t('platform.intelligence.opportunities.statusEvaluating', 'Evaluating')}</option>
          <option value="pursuing">{t('platform.intelligence.opportunities.statusPursuing', 'Pursuing')}</option>
          <option value="applied">{t('platform.intelligence.opportunities.statusApplied', 'Applied')}</option>
          <option value="won">{t('platform.intelligence.opportunities.statusWon', 'Won')}</option>
          <option value="lost">{t('platform.intelligence.opportunities.statusLost', 'Lost')}</option>
        </select>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.opportunities.totalOpportunities', 'Total Opportunities')}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{opportunities.length}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.opportunities.activelyPursuing', 'Actively Pursuing')}</p>
          <p className="text-2xl font-bold text-purple-600 mt-1">
            {opportunities.filter(o => o.status === 'pursuing' || o.status === 'applied').length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.opportunities.highMatch', 'High Match (80%+)')}</p>
          <p className="text-2xl font-bold text-green-600 mt-1">
            {opportunities.filter(o => o.matchScore >= 80).length}
          </p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-sm text-gray-600">{t('platform.intelligence.opportunities.closingSoon', 'Closing Soon')}</p>
          <p className="text-2xl font-bold text-orange-600 mt-1">
            {opportunities.filter(o => {
              if (!o.deadline) return false;
              const days = Math.ceil((new Date(o.deadline).getTime() - Date.now()) / (1000 * 60 * 60 * 24));
              return days > 0 && days <= 30;
            }).length}
          </p>
        </div>
      </div>

      {/* Opportunities List */}
      <div className="space-y-4">
        {filteredOpportunities.map((opp) => {
          const isExpanded = expandedCards.has(opp.id);
          const daysUntilDeadline = getDaysUntilDeadline(opp.deadline);

          return (
            <div
              key={opp.id}
              className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow"
            >
              {/* Header Row */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <h4 className="text-lg font-semibold text-gray-900">{opp.name}</h4>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(opp.status)}`}>
                      {opp.status.charAt(0).toUpperCase() + opp.status.slice(1)}
                    </span>
                    {/* Enrollment Growth Badge */}
                    {opp.enrollmentTrendDirection && opp.enrollmentGrowthRate12mo !== undefined && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 ${
                        opp.enrollmentTrendDirection === 'growing'
                          ? 'bg-green-100 text-green-800'
                          : opp.enrollmentTrendDirection === 'declining'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}>
                        <TrendingUp className={`h-3 w-3 ${opp.enrollmentTrendDirection === 'declining' ? 'rotate-180' : ''}`} />
                        {opp.enrollmentTrendDirection === 'growing' ? '+' : ''}
                        {Math.abs(opp.enrollmentGrowthRate12mo).toFixed(1)}% 12mo
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600">
                    <span className="flex items-center gap-1">
                      <Building2 className="h-4 w-4" />
                      {opp.organization}
                    </span>
                    {opp.sponsorType && (
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getSponsorTypeBadgeColor(opp.sponsorType)}`}>
                        {opp.sponsorType.replace(/_/g, ' ')}
                      </span>
                    )}
                    {opp.geography.length > 0 && (
                      <span className="flex items-center gap-1">
                        <MapPin className="h-4 w-4" />
                        {opp.geography.slice(0, 2).join(', ')}{opp.geography.length > 2 ? ` +${opp.geography.length - 2}` : ''}
                      </span>
                    )}
                  </div>
                </div>

                {/* Quick Interest Button & Deadline Badge */}
                <div className="flex items-center gap-3">
                  {/* Quick Flag/Interest Icon with Count */}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleFavorite(opp);
                      }}
                      className={`p-2 rounded-full transition-colors ${
                        favoritedPrograms.has(opp.id)
                          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                          : 'hover:bg-gray-100 text-gray-400 hover:text-blue-600'
                      }`}
                      title={favoritedPrograms.has(opp.id) ? "Remove flag" : "Flag interest"}
                    >
                      <Flag className={`h-5 w-5 ${favoritedPrograms.has(opp.id) ? 'fill-current' : ''}`} />
                    </button>
                    {/* Interest count */}
                    {((opp.totalInterestedCbos || 0) + (favoritedPrograms.has(opp.id) ? 1 : 0)) > 0 && (
                      <span className="text-xs text-gray-500 font-medium">
                        {(opp.totalInterestedCbos || 0) + (favoritedPrograms.has(opp.id) ? 1 : 0)}
                      </span>
                    )}
                  </div>

                  {/* Deadline Badge */}
                  {daysUntilDeadline !== null && (
                    <div className={`text-right ${daysUntilDeadline <= 7 ? 'text-red-600' : daysUntilDeadline <= 30 ? 'text-orange-600' : 'text-gray-600'}`}>
                      <div className="flex items-center gap-1 text-sm font-medium">
                        <Clock className="h-4 w-4" />
                        {daysUntilDeadline > 0 ? t('platform.intelligence.opportunities.daysLeft', '{{days}} days left', { days: daysUntilDeadline }) : t('platform.intelligence.opportunities.expired', 'Expired')}
                      </div>
                      <div className="text-xs">
                        {t('platform.intelligence.opportunities.deadline', 'Deadline')}: {formatDate(opp.deadline)}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Key Metrics Row */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">{t('platform.intelligence.opportunities.targetEnrollment', 'Target Enrollment')}</div>
                  <div className="text-lg font-semibold text-gray-900">
                    {opp.targetEnrollment?.toLocaleString() || t('platform.intelligence.opportunities.tbd', 'TBD')}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">{t('platform.intelligence.opportunities.perMemberValue', 'Per Member Value')}</div>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(opp.estimatedPerMemberValue)}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">{t('platform.intelligence.opportunities.interestedCbos', 'Interested CBOs')}</div>
                  <div className="text-lg font-semibold text-purple-600">
                    {opp.totalInterestedCbos || 0}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">{t('platform.intelligence.opportunities.readinessScore', 'Readiness Score')}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${opp.executionReadinessScore || 0}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium">
                      {Math.round(opp.executionReadinessScore || 0)}%
                    </span>
                  </div>
                </div>
              </div>

              {/* Services & Requirements Summary */}
              <div className="mb-4">
                {opp.serviceCategories && opp.serviceCategories.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-2">
                    {opp.serviceCategories.slice(0, 4).map((service, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs">
                        {service.replace(/_/g, ' ')}
                      </span>
                    ))}
                    {opp.serviceCategories.length > 4 && (
                      <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                        +{opp.serviceCategories.length - 4} more
                      </span>
                    )}
                  </div>
                )}

                {/* Provider Needs & Specialty Gaps Alert */}
                {(opp.numberOfProvidersNeeded || (opp.prioritySpecialtiesNeeded && opp.prioritySpecialtiesNeeded.length > 0)) && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-2">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-amber-900 mb-1">
                          {t('platform.intelligence.opportunities.providerNetworkOpportunity', 'Provider Network Opportunity')}
                        </div>
                        {opp.numberOfProvidersNeeded && (
                          <div className="text-xs text-amber-700">
                            {t('platform.intelligence.opportunities.seekingProviders', 'Seeking')} <strong>{opp.numberOfProvidersNeeded}</strong> {t('platform.intelligence.opportunities.providers', 'providers')}
                          </div>
                        )}
                        {opp.prioritySpecialtiesNeeded && opp.prioritySpecialtiesNeeded.length > 0 && (
                          <div className="mt-1 flex flex-wrap gap-1">
                            {opp.prioritySpecialtiesNeeded.slice(0, 5).map((specialty, idx) => (
                              <span key={idx} className="text-xs bg-amber-100 text-amber-800 px-2 py-0.5 rounded">
                                {specialty}
                              </span>
                            ))}
                            {opp.prioritySpecialtiesNeeded.length > 5 && (
                              <span className="text-xs text-amber-700">
                                +{opp.prioritySpecialtiesNeeded.length - 5} more
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {opp.description && (
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {opp.description}
                  </p>
                )}
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="border-t pt-4 mt-4 space-y-4">
                  {/* Program Classification & Risk */}
                  {opp.riskArrangement && (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.programType', 'Program Type')}</div>
                        <div className="text-sm font-medium">{getTypeLabel(opp.type)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 flex items-center gap-1">
                          <Shield className="h-3 w-3" />
                          {t('platform.intelligence.opportunities.riskArrangement', 'Risk Arrangement')}
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          opp.riskArrangement === 'two_sided' ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {opp.riskArrangement.replace(/_/g, ' ')}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Eligibility Criteria */}
                  {opp.eligibilityCriteria && Object.keys(opp.eligibilityCriteria).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <Users className="h-4 w-4" />
                        {t('platform.intelligence.opportunities.eligibilityCriteria', 'Eligibility Criteria')}
                      </h4>
                      <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                        {opp.eligibilityCriteria.insurance_types && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">{t('platform.intelligence.opportunities.insurance', 'Insurance')}:</span>
                            <div className="flex flex-wrap gap-1">
                              {opp.eligibilityCriteria.insurance_types.map((type, idx) => (
                                <span key={idx} className="px-2 py-0.5 bg-white rounded text-xs">
                                  {type}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {opp.eligibilityCriteria.age_min && (
                          <div className="text-xs">
                            <span className="text-gray-500">{t('platform.intelligence.opportunities.minimumAge', 'Minimum Age')}:</span>
                            <span className="ml-1 font-medium">{opp.eligibilityCriteria.age_min}+</span>
                          </div>
                        )}
                        {opp.eligibilityCriteria.attribution_method && (
                          <div className="text-xs">
                            <span className="text-gray-500">{t('platform.intelligence.opportunities.attribution', 'Attribution')}:</span>
                            <span className="ml-1 font-medium">{opp.eligibilityCriteria.attribution_method}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Covered Services */}
                  {opp.coveredServices && opp.coveredServices.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <Activity className="h-4 w-4" />
                        {t('platform.intelligence.opportunities.coveredServices', 'Covered Services')}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {opp.coveredServices.map((service, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-purple-100 text-purple-800 rounded text-xs">
                            {service}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Geographic Coverage */}
                  {opp.geography.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <Globe className="h-4 w-4" />
                        {t('platform.intelligence.opportunities.serviceArea', 'Service Area')}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {opp.geography.map((state, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">
                            {state}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Quality & Financial Performance */}
                  {(opp.qualityPerformance || opp.financialPerformance) && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {opp.qualityPerformance?.quality_score && (
                        <div className="bg-green-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.qualityScore', 'Quality Score')}</div>
                          <div className="text-lg font-semibold text-green-600">
                            {opp.qualityPerformance.quality_score}%
                          </div>
                        </div>
                      )}
                      {opp.financialPerformance?.savings_rate && (
                        <div className="bg-blue-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.savingsRate', 'Savings Rate')}</div>
                          <div className="text-lg font-semibold text-blue-600">
                            {opp.financialPerformance.savings_rate}%
                          </div>
                        </div>
                      )}
                      {opp.financialPerformance?.generated_savings_losses && (
                        <div className="bg-yellow-50 rounded-lg p-3">
                          <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.generatedSavings', 'Generated Savings')}</div>
                          <div className="text-sm font-semibold text-yellow-700">
                            {formatCurrency(opp.financialPerformance.generated_savings_losses)}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Required Certifications */}
                  {opp.requiredCertifications && opp.requiredCertifications.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <CheckCircle className="h-4 w-4" />
                        {t('platform.intelligence.opportunities.requiredCertifications', 'Required Certifications')}
                      </h4>
                      <div className="flex flex-wrap gap-1">
                        {opp.requiredCertifications.map((cert, idx) => (
                          <span key={idx} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs border">
                            {cert}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Reimbursement Details */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {opp.reimbursementModel && (
                      <div>
                        <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.reimbursementModel', 'Reimbursement Model')}</div>
                        <div className="text-sm font-medium">{opp.reimbursementModel.replace(/_/g, ' ')}</div>
                      </div>
                    )}
                    {opp.baseReimbursementRate && (
                      <div>
                        <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.baseRate', 'Base Rate')}</div>
                        <div className="text-sm font-medium">{formatCurrency(opp.baseReimbursementRate)}</div>
                      </div>
                    )}
                    {opp.totalProgramBudget && (
                      <div>
                        <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.totalBudget', 'Total Budget')}</div>
                        <div className="text-sm font-medium">{formatCurrency(opp.totalProgramBudget)}</div>
                      </div>
                    )}
                  </div>

                  {/* Timeline */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.programStart', 'Program Start')}</div>
                      <div className="text-sm font-medium">{formatDate(opp.programStartDate)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.programEnd', 'Program End')}</div>
                      <div className="text-sm font-medium">{formatDate(opp.programEndDate)}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-500">{t('platform.intelligence.opportunities.dataSource', 'Data Source')}</div>
                      {opp.sourceUrl ? (
                        <a
                          href={opp.sourceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                        >
                          {opp.source || t('platform.intelligence.opportunities.viewSource', 'View Source')}
                          <ExternalLink className="h-3 w-3" />
                        </a>
                      ) : (
                        <div className="text-sm font-medium">{opp.source || t('platform.intelligence.common.notAvailable', 'N/A')}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Actions Row */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t">
                <button
                  onClick={() => toggleCardExpansion(opp.id)}
                  className="text-sm text-gray-600 hover:text-gray-900 flex items-center gap-1"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="h-4 w-4" />
                      {t('platform.intelligence.opportunities.showLess', 'Show Less')}
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      {t('platform.intelligence.opportunities.showDetails', 'Show Details')}
                    </>
                  )}
                </button>

                <div className="flex items-center gap-3">
                  {/* Status Dropdown */}
                  <select
                    value={opp.status}
                    onChange={(e) => handleUpdateStatus(opp.id, e.target.value as OpportunityStatus)}
                    className={`px-3 py-1.5 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 ${getStatusColor(opp.status)}`}
                  >
                    <option value="discovered">{t('platform.intelligence.opportunities.statusDiscovered', 'Discovered')}</option>
                    <option value="evaluating">{t('platform.intelligence.opportunities.statusEvaluating', 'Evaluating')}</option>
                    <option value="pursuing">{t('platform.intelligence.opportunities.statusPursuing', 'Pursuing')}</option>
                    <option value="applied">{t('platform.intelligence.opportunities.statusApplied', 'Applied')}</option>
                    <option value="won">{t('platform.intelligence.opportunities.statusWon', 'Won')}</option>
                    <option value="lost">{t('platform.intelligence.opportunities.statusLost', 'Lost')}</option>
                  </select>

                  {/* Match Score */}
                  <div className={`text-sm font-bold ${
                    opp.matchScore >= 80 ? 'text-green-600' : opp.matchScore >= 60 ? 'text-yellow-600' : 'text-gray-600'
                  }`}>
                    {opp.matchScore}% {t('platform.intelligence.opportunities.match', 'match')}
                  </div>

                  {/* Add to Pipeline Button */}
                  <button
                    onClick={() => handleAddToPipeline(opp)}
                    className="flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                    title={t('platform.intelligence.opportunities.addToPipeline', 'Add to Sales Pipeline')}
                  >
                    <TrendingUp className="h-4 w-4" />
                    {t('platform.intelligence.opportunities.addToPipeline', 'Add to Pipeline')}
                  </button>

                  <button
                    onClick={() => handleExpressInterest(opp)}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                  >
                    <Send className="h-4 w-4" />
                    {t('platform.intelligence.opportunities.expressInterest', 'Express Interest')}
                  </button>
                </div>
              </div>
            </div>
          );
        })}

        {filteredOpportunities.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            <Globe className="h-12 w-12 mx-auto mb-3 text-gray-300" />
            <p>{t('platform.intelligence.opportunities.noOpportunities', 'No opportunities match your filters')}</p>
          </div>
        )}
      </div>

      {/* Express Interest Modal */}
      <Dialog open={showInterestModal} onOpenChange={setShowInterestModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Target className="h-5 w-5 text-purple-600" />
              {t('platform.intelligence.dialogs.expressInterest.title', 'Express Interest')}
            </DialogTitle>
            <DialogDescription>
              {t('platform.intelligence.dialogs.expressInterest.description', "Let {{organization}} know you're interested in {{name}}", { organization: selectedOpportunity?.organization, name: selectedOpportunity?.name })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Interest Level */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('platform.intelligence.dialogs.expressInterest.interestLevel', 'Interest Level')} *
              </label>
              <select
                value={interestLevel}
                onChange={(e) => setInterestLevel(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="HIGH">{t('platform.intelligence.dialogs.expressInterest.highInterest', 'High - Ready to participate')}</option>
                <option value="MEDIUM">{t('platform.intelligence.dialogs.expressInterest.mediumInterest', 'Medium - Need more information')}</option>
                <option value="EXPLORATORY">{t('platform.intelligence.dialogs.expressInterest.exploratoryInterest', 'Exploratory - Learning about it')}</option>
              </select>
            </div>

            {/* Interest Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('platform.intelligence.dialogs.expressInterest.interestType', 'Interest Type')}
              </label>
              <select
                value={interestType}
                onChange={(e) => setInterestType(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              >
                <option value="PARTICIPATE">{t('platform.intelligence.dialogs.expressInterest.directParticipation', 'Direct Participation')}</option>
                <option value="PARTNER">{t('platform.intelligence.dialogs.expressInterest.partnershipWithCbos', 'Partnership with Other CBOs')}</option>
                <option value="SUBCONTRACT">{t('platform.intelligence.dialogs.expressInterest.subcontractServices', 'Subcontract Services')}</option>
                <option value="LEARN_MORE">{t('platform.intelligence.dialogs.expressInterest.learnMoreFirst', 'Learn More First')}</option>
              </select>
            </div>

            {/* Declared Capacity */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('platform.intelligence.dialogs.expressInterest.membersYouCanServe', 'Members You Can Serve')}
              </label>
              <input
                type="number"
                value={declaredCapacity}
                onChange={(e) => setDeclaredCapacity(parseInt(e.target.value) || 0)}
                placeholder={t('platform.intelligence.dialogs.expressInterest.membersPlaceholder', 'e.g., 500')}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 mt-1">
                {t('platform.intelligence.dialogs.expressInterest.membersHelp', 'Estimated number of members your organization can serve')}
              </p>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('platform.intelligence.dialogs.expressInterest.notesOrQuestions', 'Notes or Questions')}
              </label>
              <textarea
                value={interestNotes}
                onChange={(e) => setInterestNotes(e.target.value)}
                placeholder={t('platform.intelligence.dialogs.expressInterest.notesPlaceholder', 'Share your capabilities, questions, or comments...')}
                rows={3}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setShowInterestModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              onClick={submitInterest}
              disabled={submittingInterest}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {submittingInterest ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('platform.intelligence.dialogs.expressInterest.submitting', 'Submitting...')}
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {t('platform.intelligence.dialogs.expressInterest.submitInterest', 'Submit Interest')}
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add to Sales Pipeline Modal */}
      <Dialog open={showPipelineModal} onOpenChange={setShowPipelineModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-blue-600" />
              {t('platform.intelligence.dialogs.pipeline.title', 'Add to Sales Pipeline')}
            </DialogTitle>
            <DialogDescription>
              {t('platform.intelligence.dialogs.pipeline.description', 'Track {{name}} as a sales opportunity', { name: selectedOpportunity?.name })}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Opportunity Summary */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="font-medium text-gray-900">{selectedOpportunity?.name}</h4>
              <p className="text-sm text-gray-600">{selectedOpportunity?.organization}</p>
              {selectedOpportunity?.budget && (
                <p className="text-sm text-green-600 mt-1">
                  {t('platform.intelligence.dialogs.pipeline.estimatedValue', 'Est. Value')}: ${((selectedOpportunity.budget.min + selectedOpportunity.budget.max) / 2).toLocaleString()}
                </p>
              )}
            </div>

            {/* Pipeline Stage */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('platform.intelligence.dialogs.pipeline.pipelineStage', 'Pipeline Stage')} *
              </label>
              <select
                value={pipelineStage}
                onChange={(e) => setPipelineStage(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="prospecting">{t('platform.intelligence.dialogs.pipeline.prospecting', 'Prospecting')}</option>
                <option value="qualification">{t('platform.intelligence.dialogs.pipeline.qualification', 'Qualification')}</option>
                <option value="demo">{t('platform.intelligence.dialogs.pipeline.demoScheduled', 'Demo Scheduled')}</option>
                <option value="proposal">{t('platform.intelligence.dialogs.pipeline.proposalSent', 'Proposal Sent')}</option>
                <option value="negotiation">{t('platform.intelligence.dialogs.pipeline.negotiation', 'Negotiation')}</option>
              </select>
            </div>

            {/* Assigned Owner */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('platform.intelligence.dialogs.pipeline.assignTo', 'Assign To')}
              </label>
              <select
                value={pipelineOwner}
                onChange={(e) => setPipelineOwner(e.target.value)}
                className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {staffUsers.length > 0 ? (
                  <>
                    <option value="">{t('platform.intelligence.dialogs.pipeline.unassigned', 'Unassigned')}</option>
                    {staffUsers.map(u => (
                      <option key={u.id} value={u.name}>{u.name}</option>
                    ))}
                  </>
                ) : (
                  <option value="" disabled>{t('platform.common.noUsersAvailable', 'No users available')}</option>
                )}
              </select>
            </div>

            {/* Source Info */}
            <div className="text-sm text-gray-500 border-t pt-3">
              <p>{t('platform.intelligence.dialogs.pipeline.source', 'Source')}: {selectedOpportunity?.source}</p>
              <p>{t('platform.intelligence.dialogs.pipeline.type', 'Type')}: {selectedOpportunity?.type?.replace('_', ' ')}</p>
              {selectedOpportunity?.deadline && (
                <p>{t('platform.intelligence.dialogs.pipeline.deadline', 'Deadline')}: {new Date(selectedOpportunity.deadline).toLocaleDateString()}</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setShowPipelineModal(false)}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              onClick={submitToPipeline}
              disabled={addingToPipeline}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {addingToPipeline ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('platform.intelligence.dialogs.pipeline.adding', 'Adding...')}
                </>
              ) : (
                <>
                  <TrendingUp className="h-4 w-4" />
                  {t('platform.intelligence.dialogs.pipeline.addToPipeline', 'Add to Pipeline')}
                </>
              )}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============================================================================
// INTELLIGENCE SEARCH TAB (Natural Language Query)
// ============================================================================

interface SearchResult {
  id: string;
  type: 'signal' | 'opportunity' | 'vendor' | 'capability';
  title: string;
  subtitle: string;
  relevanceScore: number;
  matchedTerms: string[];
  data: Record<string, unknown>;
}


interface SettingsTabProps {
  rules: IntentRule[];
  orgSettings: OrganizationIntentSettings;
  onCreateRule: () => void;
  onToggleRule: (ruleId: string) => void;
  onDeleteRule: (ruleId: string) => void;
  onUpdateOrgSettings: (settings: Partial<OrganizationIntentSettings>) => void;
}

function SettingsTab({
  rules,
  orgSettings,
  onCreateRule,
  onToggleRule,
  onDeleteRule,
  onUpdateOrgSettings
}: SettingsTabProps) {
  const { t } = useAppTranslation();
  const [activeSection, setActiveSection] = useState<'mode' | 'rules' | 'matching'>('mode');

  return (
    <div>
      {/* Section Navigation */}
      <div className="flex items-center gap-4 mb-6 border-b border-gray-200">
        <button
          onClick={() => setActiveSection('mode')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeSection === 'mode'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('platform.intelligence.settings.organizationMode', 'Organization Mode')}
        </button>
        <button
          onClick={() => setActiveSection('rules')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeSection === 'rules'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('platform.intelligence.settings.matchingRules', 'Matching Rules')}
        </button>
        <button
          onClick={() => setActiveSection('matching')}
          className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
            activeSection === 'matching'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          {t('platform.intelligence.settings.matchSettings', 'Match Settings')}
        </button>
      </div>

      {/* Organization Mode Section */}
      {activeSection === 'mode' && (
        <div className="space-y-6">
          {/* Current Mode Verification Banner */}
          <div className={`rounded-lg border-2 p-4 ${
            orgSettings.mode === 'single-vendor'
              ? 'bg-green-50 border-green-200'
              : 'bg-blue-50 border-blue-200'
          }`}>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${
                orgSettings.mode === 'single-vendor' ? 'bg-green-100' : 'bg-blue-100'
              }`}>
                <Shield className={`h-5 w-5 ${
                  orgSettings.mode === 'single-vendor' ? 'text-green-600' : 'text-blue-600'
                }`} />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <h4 className={`font-semibold ${
                    orgSettings.mode === 'single-vendor' ? 'text-green-800' : 'text-blue-800'
                  }`}>
                    {t('platform.intelligence.settings.currentMode', 'Current Mode')}: {orgSettings.mode === 'single-vendor' ? t('platform.intelligence.settings.singleVendor', 'Single Vendor') : t('platform.intelligence.settings.systemsIntegrator', 'Systems Integrator')}
                  </h4>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                    orgSettings.mode === 'single-vendor'
                      ? 'bg-green-200 text-green-700'
                      : 'bg-blue-200 text-blue-700'
                  }`}>
                    {t('platform.intelligence.settings.verified', 'Verified')}
                  </span>
                </div>
                <p className={`text-sm mt-1 ${
                  orgSettings.mode === 'single-vendor' ? 'text-green-700' : 'text-blue-700'
                }`}>
                  {orgSettings.mode === 'single-vendor'
                    ? t('platform.intelligence.settings.singleVendorDescription', '{{orgName}} is configured as a single vendor. The Capabilities tab displays YOUR products and services that prospects can match with.', { orgName: orgSettings.organizationName })
                    : t('platform.intelligence.settings.integratorDescription', '{{orgName}} is configured as a systems integrator. The Vendors tab displays third-party solutions you recommend to customers.', { orgName: orgSettings.organizationName })
                  }
                </p>
              </div>
              <CheckCircle className={`h-6 w-6 ${
                orgSettings.mode === 'single-vendor' ? 'text-green-500' : 'text-blue-500'
              }`} />
            </div>
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('platform.intelligence.settings.organizationMode', 'Organization Mode')}</h3>
            <p className="text-sm text-gray-500 mb-6">
              {t('platform.intelligence.settings.organizationModeDescription', 'Configure how Intent Intelligence works based on your organization type')}
            </p>
          </div>

          {/* Mode Selection Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Single Vendor Mode */}
            <button
              onClick={() => onUpdateOrgSettings({ mode: 'single-vendor' })}
              className={`p-6 rounded-lg border-2 text-left transition-all ${
                orgSettings.mode === 'single-vendor'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  orgSettings.mode === 'single-vendor' ? 'bg-purple-100' : 'bg-gray-100'
                }`}>
                  <Building2 className={`h-6 w-6 ${
                    orgSettings.mode === 'single-vendor' ? 'text-purple-600' : 'text-gray-600'
                  }`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900">{t('platform.intelligence.settings.singleVendorMode', 'Single Vendor Mode')}</h4>
                    {orgSettings.mode === 'single-vendor' && (
                      <CheckCircle className="h-5 w-5 text-purple-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('platform.intelligence.settings.singleVendorModeDescription', 'Your organization IS the vendor. Define your own product/service capabilities and match them with buying signals from prospects.')}
                  </p>
                  <div className="space-y-1 text-xs text-gray-500">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {t('platform.intelligence.settings.defineCapabilities', 'Define your capabilities (products, services, features)')}
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {t('platform.intelligence.settings.matchSignalsToOfferings', 'Match buying signals to YOUR offerings')}
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {t('platform.intelligence.settings.bestForSaas', 'Best for: SaaS companies, solution providers')}
                    </p>
                  </div>
                </div>
              </div>
            </button>

            {/* Systems Integrator Mode */}
            <button
              onClick={() => onUpdateOrgSettings({ mode: 'systems-integrator' })}
              className={`p-6 rounded-lg border-2 text-left transition-all ${
                orgSettings.mode === 'systems-integrator'
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-gray-300 bg-white'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`p-3 rounded-lg ${
                  orgSettings.mode === 'systems-integrator' ? 'bg-purple-100' : 'bg-gray-100'
                }`}>
                  <Users className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h4 className="font-semibold text-gray-900">{t('platform.intelligence.settings.systemsIntegratorMode', 'Systems Integrator Mode')}</h4>
                    {orgSettings.mode === 'systems-integrator' && (
                      <CheckCircle className="h-5 w-5 text-purple-600" />
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-3">
                    {t('platform.intelligence.settings.integratorModeDescription', 'Your organization helps customers find solutions from multiple vendors. Manage vendor ecosystem and match customer needs to vendor capabilities.')}
                  </p>
                  <div className="space-y-1 text-xs text-gray-500">
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {t('platform.intelligence.settings.manageVendorPartners', 'Manage multiple vendor partners')}
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {t('platform.intelligence.settings.matchNeedsToCapabilities', 'Match customer needs to vendor capabilities')}
                    </p>
                    <p className="flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {t('platform.intelligence.settings.bestForConsultants', 'Best for: Consultants, SI firms, resellers')}
                    </p>
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Organization Details */}
          <div className="bg-white rounded-lg border border-gray-200 p-6 mt-6">
            <h4 className="font-medium text-gray-900 mb-4">{t('platform.intelligence.settings.organizationDetails', 'Organization Details')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('platform.intelligence.settings.organizationName', 'Organization Name')}
                </label>
                <input
                  type="text"
                  value={orgSettings.organizationName}
                  onChange={(e) => onUpdateOrgSettings({ organizationName: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('platform.intelligence.settings.primaryIndustries', 'Primary Industries')}
                </label>
                <input
                  type="text"
                  value={orgSettings.primaryIndustries.join(', ')}
                  onChange={(e) => onUpdateOrgSettings({
                    primaryIndustries: e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                  })}
                  placeholder={t('platform.intelligence.settings.industriesPlaceholder', 'Healthcare, Insurance, Benefits')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('platform.intelligence.settings.description', 'Description')}
                </label>
                <textarea
                  value={orgSettings.organizationDescription || ''}
                  onChange={(e) => onUpdateOrgSettings({ organizationDescription: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Matching Rules Section */}
      {activeSection === 'rules' && (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">{t('platform.intelligence.settings.matchingRules', 'Matching Rules')}</h3>
            <button
              onClick={onCreateRule}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="h-4 w-4" />
              {t('platform.intelligence.settings.createRule', 'Create Rule')}
            </button>
          </div>

          <div className="space-y-4">
            {rules.map((rule) => (
              <div key={rule.id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <div className="flex items-center gap-3 mb-1">
                      <h4 className="font-semibold text-gray-900">{rule.name}</h4>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                        rule.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {rule.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500">
                      {t('platform.intelligence.settings.trigger', 'Trigger')}: {rule.trigger}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onToggleRule(rule.id)}
                      className={`px-3 py-1.5 text-sm rounded-lg ${
                        rule.status === 'active'
                          ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200'
                          : 'bg-green-100 text-green-700 hover:bg-green-200'
                      }`}
                    >
                      {rule.status === 'active' ? t('platform.intelligence.settings.pause', 'Pause') : t('platform.intelligence.settings.activate', 'Activate')}
                    </button>
                    <button className="p-1.5 hover:bg-gray-100 rounded">
                      <Edit className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => onDeleteRule(rule.id)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">{t('platform.intelligence.settings.conditions', 'Conditions')}</h5>
                    <ul className="space-y-1">
                      {rule.conditions.map((condition, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <Filter className="h-3 w-3 text-gray-400" />
                          {condition}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <h5 className="text-xs font-medium text-gray-500 uppercase mb-2">{t('platform.intelligence.settings.actions', 'Actions')}</h5>
                    <ul className="space-y-1">
                      {rule.actions.map((action, i) => (
                        <li key={i} className="flex items-center gap-2 text-sm text-gray-600">
                          <Zap className="h-3 w-3 text-blue-500" />
                          {action}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-100 text-sm text-gray-500">
                  {t('platform.intelligence.settings.matchesCreated', '{{count}} matches created by this rule', { count: rule.matches })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Match Settings Section */}
      {activeSection === 'matching' && (
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('platform.intelligence.settings.matchSettings', 'Match Settings')}</h3>
            <p className="text-sm text-gray-500 mb-6">
              {t('platform.intelligence.settings.matchSettingsDescription', 'Configure automatic matching behavior')}
            </p>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-6 space-y-6">
            {/* Auto Match Toggle */}
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-medium text-gray-900">{t('platform.intelligence.settings.automaticMatching', 'Automatic Matching')}</h4>
                <p className="text-sm text-gray-500">
                  {t('platform.intelligence.settings.automaticMatchingDescription', 'Automatically create matches when signals meet threshold')}
                </p>
              </div>
              <button
                onClick={() => onUpdateOrgSettings({ autoMatchEnabled: !orgSettings.autoMatchEnabled })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  orgSettings.autoMatchEnabled ? 'bg-purple-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    orgSettings.autoMatchEnabled ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Match Threshold */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{t('platform.intelligence.settings.matchThreshold', 'Match Threshold')}</h4>
                <span className="text-sm font-medium text-purple-600">{orgSettings.matchThreshold}%</span>
              </div>
              <p className="text-sm text-gray-500 mb-3">
                {t('platform.intelligence.settings.minimumScoreRequired', 'Minimum score required to create a match')}
              </p>
              <input
                type="range"
                min="0"
                max="100"
                value={orgSettings.matchThreshold}
                onChange={(e) => onUpdateOrgSettings({ matchThreshold: parseInt(e.target.value) })}
                className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-purple-600"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>{t('platform.intelligence.settings.matchAll', '0% (Match all)')}</span>
                <span>50%</span>
                <span>{t('platform.intelligence.settings.exactMatch', '100% (Exact match)')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// DATA SOURCES TAB & MODALS
// ============================================================================

interface AddDataSourceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (dataSource: Omit<DataSource, 'id' | 'recordsImported' | 'lastSync'>) => void;
  category: DataSourceCategory;
}

function AddDataSourceModal({ isOpen, onClose, onSubmit, category }: AddDataSourceModalProps) {
  const { t } = useAppTranslation();
  const [selectedType, setSelectedType] = useState<DataSourceType | null>(null);
  const [name, setName] = useState('');
  const [formData, setFormData] = useState<Record<string, string>>({});
  const [step, setStep] = useState<'select' | 'configure'>('select');

  const dataSourceTypes = category === 'traffic'
    ? TRAFFIC_DATA_SOURCE_TYPES
    : category === 'external-vendor'
      ? EXTERNAL_VENDOR_DATA_SOURCE_TYPES
      : SELF_MANAGED_DATA_SOURCE_TYPES;

  const categoryLabels = {
    'traffic': t('platform.intelligence.dataSources.trafficData', 'Traffic Data'),
    'external-vendor': t('platform.intelligence.dataSources.externalIntentVendor', 'External Intent Vendor'),
    'self-managed': t('platform.intelligence.dataSources.selfManagedDataSource', 'Self-Managed Data Source'),
  };

  const handleTypeSelect = (type: DataSourceType) => {
    setSelectedType(type);
    setName(type.name);
    setStep('configure');
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (selectedType) {
      onSubmit({
        name,
        category,
        type: selectedType.id,
        description: selectedType.description,
        status: 'pending',
        config: formData,
      });
    }
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setSelectedType(null);
    setName('');
    setFormData({});
    setStep('select');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {step === 'select' ? t('platform.intelligence.dataSources.addCategory', 'Add {{category}}', { category: categoryLabels[category] }) : t('platform.intelligence.dataSources.configureType', 'Configure {{type}}', { type: selectedType?.name })}
          </DialogTitle>
          <DialogDescription>
            {step === 'select'
              ? t('platform.intelligence.dataSources.selectDataSourceType', 'Select the type of data source you want to connect')
              : t('platform.intelligence.dataSources.enterConnectionDetails', 'Enter the connection details for this data source')}
          </DialogDescription>
        </DialogHeader>

        {step === 'select' ? (
          <div className="py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-[400px] overflow-y-auto">
              {dataSourceTypes.map((type) => (
                <button
                  key={type.id}
                  onClick={() => handleTypeSelect(type)}
                  className="flex items-start gap-3 p-4 border border-gray-200 rounded-lg hover:border-blue-500 hover:bg-blue-50 text-left transition-colors"
                >
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Database className="h-5 w-5 text-gray-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-gray-900 truncate">{type.name}</h4>
                    <p className="text-xs text-gray-500 line-clamp-2">{type.description}</p>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('platform.intelligence.dataSources.displayName', 'Display Name')} *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {selectedType?.requiredFields.map((field) => (
              <div key={field.key}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label} *
                </label>
                {field.type === 'select' ? (
                  <select
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select...</option>
                    {field.options?.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : field.type === 'textarea' ? (
                  <textarea
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    required
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                ) : (
                  <input
                    type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                    value={formData[field.key] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                    placeholder={field.placeholder}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                )}
              </div>
            ))}

            {selectedType?.optionalFields && selectedType.optionalFields.length > 0 && (
              <>
                <div className="border-t border-gray-200 pt-4 mt-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-3">{t('platform.intelligence.dataSources.optionalSettings', 'Optional Settings')}</h4>
                </div>
                {selectedType.optionalFields.map((field) => (
                  <div key={field.key}>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {field.label}
                    </label>
                    {field.type === 'select' ? (
                      <select
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select...</option>
                        {field.options?.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        placeholder={field.placeholder}
                        rows={2}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    ) : field.type === 'checkbox' ? (
                      <input
                        type="checkbox"
                        checked={formData[field.key] === 'true'}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.checked ? 'true' : 'false' })}
                        className="h-4 w-4 text-blue-600 rounded border-gray-300"
                      />
                    ) : (
                      <input
                        type={field.type === 'password' ? 'password' : field.type === 'number' ? 'number' : 'text'}
                        value={formData[field.key] || ''}
                        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
                        placeholder={field.placeholder}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    )}
                  </div>
                ))}
              </>
            )}

            <DialogFooter className="pt-4">
              <button
                type="button"
                onClick={() => setStep('select')}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('platform.intelligence.common.back', 'Back')}
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('platform.intelligence.dataSources.connectDataSource', 'Connect Data Source')}
              </button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface ViewImportJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: ImportJob | null;
}

function ViewImportJobModal({ isOpen, onClose, job }: ViewImportJobModalProps) {
  const { t } = useAppTranslation();
  if (!job) return null;

  const getStageColor = (stage: ETLStage) => {
    switch (stage) {
      case 'extraction': return 'bg-blue-500';
      case 'transformation': return 'bg-yellow-500';
      case 'validation': return 'bg-purple-500';
      case 'loading': return 'bg-green-500';
      case 'completed': return 'bg-green-600';
      case 'failed': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  const getLogLevelColor = (level: 'info' | 'warning' | 'error') => {
    switch (level) {
      case 'info': return 'text-blue-600';
      case 'warning': return 'text-yellow-600';
      case 'error': return 'text-red-600';
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dataSources.importJobDetails', 'Import Job Details')}</DialogTitle>
        </DialogHeader>
        <div className="py-4 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{job.dataSourceName}</h3>
              <p className="text-sm text-gray-500">{t('platform.intelligence.dataSources.started', 'Started')}: {new Date(job.startedAt).toLocaleString()}</p>
            </div>
            <span className={`px-3 py-1 text-sm font-medium rounded-full ${
              job.status === 'completed' ? 'bg-green-100 text-green-700' :
              job.status === 'running' ? 'bg-blue-100 text-blue-700' :
              job.status === 'failed' ? 'bg-red-100 text-red-700' :
              'bg-gray-100 text-gray-700'
            }`}>
              {job.status.charAt(0).toUpperCase() + job.status.slice(1)}
            </span>
          </div>

          {/* ETL Pipeline Progress */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">{t('platform.intelligence.dataSources.etlPipelineProgress', 'ETL Pipeline Progress')}</h4>
            <div className="flex items-center gap-2">
              {(['extraction', 'transformation', 'validation', 'loading'] as ETLStage[]).map((stage, index) => {
                const stages: ETLStage[] = ['extraction', 'transformation', 'validation', 'loading'];
                const currentIndex = stages.indexOf(job.stage);
                const stageIndex = index;
                const isActive = stageIndex === currentIndex && job.status === 'running';
                const isComplete = stageIndex < currentIndex || job.status === 'completed';
                const isFailed = job.status === 'failed' && stageIndex === currentIndex;

                return (
                  <React.Fragment key={stage}>
                    <div className={`flex-1 h-2 rounded-full ${
                      isComplete ? 'bg-green-500' :
                      isActive ? 'bg-blue-500 animate-pulse' :
                      isFailed ? 'bg-red-500' :
                      'bg-gray-200'
                    }`} />
                    {index < 3 && <ChevronRight className="h-4 w-4 text-gray-400" />}
                  </React.Fragment>
                );
              })}
            </div>
            <div className="flex justify-between mt-2 text-xs text-gray-500">
              <span>{t('platform.intelligence.dataSources.extract', 'Extract')}</span>
              <span>{t('platform.intelligence.dataSources.transform', 'Transform')}</span>
              <span>{t('platform.intelligence.dataSources.validate', 'Validate')}</span>
              <span>{t('platform.intelligence.dataSources.load', 'Load')}</span>
            </div>
          </div>

          {/* Progress Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">{t('platform.intelligence.dataSources.progress', 'Progress')}</p>
              <p className="text-lg font-bold text-gray-900">{job.progress}%</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">{t('platform.intelligence.dataSources.processed', 'Processed')}</p>
              <p className="text-lg font-bold text-gray-900">{job.recordsProcessed.toLocaleString()}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">{t('platform.intelligence.dataSources.total', 'Total')}</p>
              <p className="text-lg font-bold text-gray-900">{job.recordsTotal.toLocaleString()}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <p className="text-xs text-gray-500">{t('platform.intelligence.dataSources.failed', 'Failed')}</p>
              <p className={`text-lg font-bold ${job.recordsFailed > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                {job.recordsFailed}
              </p>
            </div>
          </div>

          {/* Error Message */}
          {job.errorMessage && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                <div>
                  <h4 className="font-medium text-red-800">{t('platform.intelligence.common.error', 'Error')}</h4>
                  <p className="text-sm text-red-700">{job.errorMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Logs */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-3">{t('platform.intelligence.dataSources.jobLogs', 'Job Logs')}</h4>
            <div className="bg-gray-900 rounded-lg p-4 max-h-48 overflow-y-auto">
              {job.logs.map((log, i) => (
                <div key={i} className="flex gap-3 text-sm font-mono mb-1">
                  <span className="text-gray-500">{new Date(log.timestamp).toLocaleTimeString()}</span>
                  <span className={`uppercase text-xs font-bold ${getLogLevelColor(log.level)}`}>
                    [{log.level}]
                  </span>
                  <span className="text-gray-300">{log.message}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            {t('platform.intelligence.common.close', 'Close')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

interface ConfigureScheduleModalProps {
  isOpen: boolean;
  onClose: () => void;
  dataSource: DataSource | null;
  onSubmit: (dataSourceId: string, frequency: ImportSchedule['frequency']) => void;
}

function ConfigureScheduleModal({ isOpen, onClose, dataSource, onSubmit }: ConfigureScheduleModalProps) {
  const { t } = useAppTranslation();
  const [frequency, setFrequency] = useState<ImportSchedule['frequency']>('daily');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (dataSource) {
      onSubmit(dataSource.id, frequency);
    }
    onClose();
  };

  if (!dataSource) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('platform.intelligence.dataSources.configureSyncSchedule', 'Configure Sync Schedule')}</DialogTitle>
          <DialogDescription>
            {t('platform.intelligence.dataSources.setHowOften', 'Set how often to sync data from {{name}}', { name: dataSource.name })}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-3">
            {[
              { value: 'realtime', label: t('platform.intelligence.dataSources.realtime', 'Real-time'), desc: t('platform.intelligence.dataSources.realtimeDesc', 'Sync data as it becomes available') },
              { value: 'hourly', label: t('platform.intelligence.dataSources.hourly', 'Hourly'), desc: t('platform.intelligence.dataSources.hourlyDesc', 'Sync every hour') },
              { value: 'daily', label: t('platform.intelligence.dataSources.daily', 'Daily'), desc: t('platform.intelligence.dataSources.dailyDesc', 'Sync once per day at midnight') },
              { value: 'weekly', label: t('platform.intelligence.dataSources.weekly', 'Weekly'), desc: t('platform.intelligence.dataSources.weeklyDesc', 'Sync once per week on Sunday') },
              { value: 'monthly', label: t('platform.intelligence.dataSources.monthly', 'Monthly'), desc: t('platform.intelligence.dataSources.monthlyDesc', 'Sync on the 1st of each month') },
              { value: 'manual', label: t('platform.intelligence.dataSources.manualOnly', 'Manual Only'), desc: t('platform.intelligence.dataSources.manualDesc', 'Only sync when manually triggered') },
            ].map((option) => (
              <label
                key={option.value}
                className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer ${
                  frequency === option.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="frequency"
                  value={option.value}
                  checked={frequency === option.value}
                  onChange={(e) => setFrequency(e.target.value as ImportSchedule['frequency'])}
                  className="h-4 w-4 text-blue-600"
                />
                <div>
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-sm text-gray-500">{option.desc}</div>
                </div>
              </label>
            ))}
          </div>
          <DialogFooter>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              {t('platform.intelligence.common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              {t('platform.intelligence.dataSources.saveSchedule', 'Save Schedule')}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

interface DataSourcesTabProps {
  dataSources: DataSource[];
  importJobs: ImportJob[];
  onAddDataSource: (category: DataSourceCategory) => void;
  onDeleteDataSource: (id: string) => void;
  onSyncDataSource: (id: string) => void;
  onConfigureSchedule: (dataSource: DataSource) => void;
  onViewJob: (job: ImportJob) => void;
}

function DataSourcesTab({
  dataSources,
  importJobs,
  onAddDataSource,
  onDeleteDataSource,
  onSyncDataSource,
  onConfigureSchedule,
  onViewJob
}: DataSourcesTabProps) {
  const { t } = useAppTranslation();
  const [activeCategory, setActiveCategory] = useState<DataSourceCategory | 'all' | 'jobs'>('all');
  const [searchTerm, setSearchTerm] = useState('');

  const getStatusBadge = (status: DataSourceStatus) => {
    switch (status) {
      case 'connected':
        return <span className="flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium"><CheckCircle className="h-3 w-3" />{t('platform.intelligence.dataSources.connected', 'Connected')}</span>;
      case 'syncing':
        return <span className="flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium"><Loader2 className="h-3 w-3 animate-spin" />{t('platform.intelligence.dataSources.syncing', 'Syncing')}</span>;
      case 'error':
        return <span className="flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium"><AlertCircle className="h-3 w-3" />{t('platform.intelligence.common.error', 'Error')}</span>;
      case 'disconnected':
        return <span className="flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium"><Radio className="h-3 w-3" />{t('platform.intelligence.dataSources.disconnected', 'Disconnected')}</span>;
      case 'pending':
        return <span className="flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium"><Clock className="h-3 w-3" />{t('platform.intelligence.dataSources.pending', 'Pending')}</span>;
    }
  };

  const getCategoryIcon = (category: DataSourceCategory) => {
    switch (category) {
      case 'traffic': return <Globe className="h-5 w-5 text-blue-600" />;
      case 'external-vendor': return <ExternalLink className="h-5 w-5 text-purple-600" />;
      case 'self-managed': return <Database className="h-5 w-5 text-green-600" />;
    }
  };

  const getCategoryLabel = (category: DataSourceCategory) => {
    switch (category) {
      case 'traffic': return t('platform.intelligence.dataSources.trafficData', 'Traffic Data');
      case 'external-vendor': return t('platform.intelligence.dataSources.externalVendor', 'External Vendor');
      case 'self-managed': return t('platform.intelligence.dataSources.selfManaged', 'Self-Managed');
    }
  };

  const filteredSources = dataSources.filter(ds => {
    const matchesSearch = ds.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ds.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = activeCategory === 'all' || activeCategory === 'jobs' || ds.category === activeCategory;
    return matchesSearch && matchesCategory;
  });

  const trafficCount = dataSources.filter(ds => ds.category === 'traffic').length;
  const externalCount = dataSources.filter(ds => ds.category === 'external-vendor').length;
  const selfManagedCount = dataSources.filter(ds => ds.category === 'self-managed').length;
  const runningJobs = importJobs.filter(j => j.status === 'running').length;

  return (
    <div>
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Globe className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.dataSources.trafficSources', 'Traffic Sources')}</p>
              <p className="text-xl font-bold text-gray-900">{trafficCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <ExternalLink className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.dataSources.externalVendors', 'External Vendors')}</p>
              <p className="text-xl font-bold text-gray-900">{externalCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Database className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.dataSources.selfManaged', 'Self-Managed')}</p>
              <p className="text-xl font-bold text-gray-900">{selfManagedCount}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Activity className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.dataSources.runningJobs', 'Running Jobs')}</p>
              <p className="text-xl font-bold text-gray-900">{runningJobs}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gray-100 rounded-lg">
              <Layers className="h-5 w-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.dataSources.totalRecords', 'Total Records')}</p>
              <p className="text-xl font-bold text-gray-900">
                {dataSources.reduce((acc, ds) => acc + ds.recordsImported, 0).toLocaleString()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Category Tabs & Actions */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-2 overflow-x-auto">
          {[
            { id: 'all', label: t('platform.intelligence.dataSources.allSources', 'All Sources'), count: dataSources.length },
            { id: 'traffic', label: t('platform.intelligence.dataSources.traffic', 'Traffic'), count: trafficCount },
            { id: 'external-vendor', label: t('platform.intelligence.dataSources.externalVendors', 'External Vendors'), count: externalCount },
            { id: 'self-managed', label: t('platform.intelligence.dataSources.selfManaged', 'Self-Managed'), count: selfManagedCount },
            { id: 'jobs', label: t('platform.intelligence.dataSources.importJobs', 'Import Jobs'), count: importJobs.length },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveCategory(tab.id as DataSourceCategory | 'all' | 'jobs')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === tab.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {tab.label} ({tab.count})
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={t('platform.intelligence.dataSources.searchSources', 'Search sources...')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="relative group">
            <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
              <Plus className="h-4 w-4" />
              {t('platform.intelligence.dataSources.addDataSource', 'Add Data Source')}
              <ChevronDown className="h-4 w-4" />
            </button>
            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
              <button
                onClick={() => onAddDataSource('traffic')}
                className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 rounded-t-lg"
              >
                <Globe className="h-5 w-5 text-blue-600" />
                <div>
                  <div className="font-medium text-gray-900">{t('platform.intelligence.dataSources.trafficData', 'Traffic Data')}</div>
                  <div className="text-xs text-gray-500">{t('platform.intelligence.dataSources.websiteAppAnalytics', 'Website & app analytics')}</div>
                </div>
              </button>
              <button
                onClick={() => onAddDataSource('external-vendor')}
                className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50"
              >
                <ExternalLink className="h-5 w-5 text-purple-600" />
                <div>
                  <div className="font-medium text-gray-900">{t('platform.intelligence.dataSources.externalVendor', 'External Vendor')}</div>
                  <div className="text-xs text-gray-500">{t('platform.intelligence.dataSources.thirdPartyIntentData', 'Third-party intent data')}</div>
                </div>
              </button>
              <button
                onClick={() => onAddDataSource('self-managed')}
                className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-gray-50 rounded-b-lg"
              >
                <Database className="h-5 w-5 text-green-600" />
                <div>
                  <div className="font-medium text-gray-900">{t('platform.intelligence.dataSources.selfManaged', 'Self-Managed')}</div>
                  <div className="text-xs text-gray-500">{t('platform.intelligence.dataSources.crmDatabasesApis', 'CRM, databases, APIs')}</div>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Data Sources List or Import Jobs */}
      {activeCategory === 'jobs' ? (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">{t('platform.intelligence.dataSources.importJobs', 'Import Jobs')}</h3>
          {importJobs.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Activity className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">{t('platform.intelligence.dataSources.noImportJobsYet', 'No import jobs yet')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {importJobs.map((job) => (
                <div key={job.id} className="bg-white rounded-lg border border-gray-200 p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        job.status === 'completed' ? 'bg-green-100' :
                        job.status === 'running' ? 'bg-blue-100' :
                        job.status === 'failed' ? 'bg-red-100' :
                        'bg-gray-100'
                      }`}>
                        {job.status === 'running' ? (
                          <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                        ) : job.status === 'completed' ? (
                          <CheckCircle className="h-5 w-5 text-green-600" />
                        ) : job.status === 'failed' ? (
                          <AlertCircle className="h-5 w-5 text-red-600" />
                        ) : (
                          <Clock className="h-5 w-5 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <h4 className="font-medium text-gray-900">{job.dataSourceName}</h4>
                        <div className="flex items-center gap-3 text-sm text-gray-500">
                          <span>{t('platform.intelligence.dataSources.started', 'Started')}: {new Date(job.startedAt).toLocaleString()}</span>
                          {job.completedAt && <span>{t('platform.intelligence.dataSources.completed', 'Completed')}: {new Date(job.completedAt).toLocaleString()}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <div className="text-sm font-medium text-gray-900">
                          {job.recordsProcessed.toLocaleString()} / {job.recordsTotal.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">{t('platform.intelligence.dataSources.recordsProcessed', 'records processed')}</div>
                      </div>
                      <div className="w-24">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              job.status === 'completed' ? 'bg-green-500' :
                              job.status === 'failed' ? 'bg-red-500' :
                              'bg-blue-500'
                            }`}
                            style={{ width: `${job.progress}%` }}
                          />
                        </div>
                        <div className="text-xs text-gray-500 text-center mt-1">{job.progress}%</div>
                      </div>
                      <button
                        onClick={() => onViewJob(job)}
                        className="p-2 hover:bg-gray-100 rounded-lg"
                        title={t('platform.intelligence.dataSources.viewDetails', 'View Details')}
                      >
                        <Eye className="h-4 w-4 text-gray-500" />
                      </button>
                    </div>
                  </div>
                  {job.errorMessage && (
                    <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                      {job.errorMessage}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredSources.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Database className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">
                {searchTerm ? t('platform.intelligence.dataSources.noSourcesMatchSearch', 'No data sources match your search') : t('platform.intelligence.dataSources.noSourcesConnectedYet', 'No data sources connected yet')}
              </p>
              <button
                onClick={() => onAddDataSource('self-managed')}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                {t('platform.intelligence.dataSources.addYourFirstDataSource', 'Add Your First Data Source')}
              </button>
            </div>
          ) : (
            filteredSources.map((source) => (
              <div key={source.id} className="bg-white rounded-lg border border-gray-200 p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="p-3 bg-gray-100 rounded-lg">
                      {getCategoryIcon(source.category)}
                    </div>
                    <div>
                      <div className="flex items-center gap-3 mb-1">
                        <h4 className="font-semibold text-gray-900">{source.name}</h4>
                        {getStatusBadge(source.status)}
                        <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded text-xs">
                          {getCategoryLabel(source.category)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{source.description}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Layers className="h-3 w-3" />
                          {t('platform.intelligence.dataSources.recordCount', '{{count}} records', { count: source.recordsImported.toLocaleString() })}
                        </span>
                        {source.lastSync && (
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {t('platform.intelligence.dataSources.lastSync', 'Last sync')}: {new Date(source.lastSync).toLocaleString()}
                          </span>
                        )}
                        {source.nextSync && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            {t('platform.intelligence.dataSources.next', 'Next')}: {new Date(source.nextSync).toLocaleString()}
                          </span>
                        )}
                      </div>
                      {source.errorMessage && (
                        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                          {source.errorMessage}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => onSyncDataSource(source.id)}
                      disabled={source.status === 'syncing'}
                      className={`flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg ${
                        source.status === 'syncing'
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                      }`}
                    >
                      {source.status === 'syncing' ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4" />
                      )}
                      {t('platform.intelligence.dataSources.syncNow', 'Sync Now')}
                    </button>
                    <button
                      onClick={() => onConfigureSchedule(source)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title={t('platform.intelligence.dataSources.configureSchedule', 'Configure Schedule')}
                    >
                      <Calendar className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title={t('platform.intelligence.dataSources.viewSettings', 'View Settings')}
                    >
                      <Settings className="h-4 w-4 text-gray-500" />
                    </button>
                    <button
                      onClick={() => onDeleteDataSource(source.id)}
                      className="p-1.5 hover:bg-gray-100 rounded"
                      title={t('platform.intelligence.common.delete', 'Delete')}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

interface IntentIntelligencePageProps {
  /**
   * Optional tab-change handler from the App shell. When provided, the
   * "Add to Pipeline" action navigates the user to the Sales Pipeline page
   * after a successful POST so they see the new opportunity on the kanban.
   */
  onNavigate?: (tab: string) => void;
  /**
   * True when rendered as a section inside OutreachWorkspace (the Funnel workspace's
   * sub-tab navigator is the header). Suppresses the standalone-only analytics assistant
   * panel so it doesn't appear inside the funnel.
   */
  embedded?: boolean;
}

export function IntentIntelligencePage({ onNavigate, embedded = false }: IntentIntelligencePageProps = {}) {
  const { t } = useAppTranslation();
  const tabbed = useTabbedChrome();
  const isTriveny = productConfig.isTriveny;
  const [activeTab, setActiveTab] = useState<IntentTab>('signals');

  // Organization Settings - controls single-vendor vs systems-integrator mode.
  // Triveny tenants get a B2B SaaS-flavored default; WeaveIn keeps the
  // healthcare configuration so existing demos and real workflows are intact.
  const [orgSettings, setOrgSettings] = useState<OrganizationIntentSettings>(
    isTriveny ? trivenyDefaultSettings : defaultOrganizationSettings,
  );

  // State for data — all domains start EMPTY and are populated from real
  // backend endpoints (fetch effects below). No hardcoded mock/demo seed:
  // seeded rows were persisted verbatim (owners, "connected" data sources)
  // and poisoned production. Domains without a backend yet (vendors,
  // capabilities, vendor matches) stay empty and their tabs are hidden —
  // see DONOR/Intelligence plan; re-enable when endpoints ship.
  const [signals, setSignals] = useState<BuyingSignal[]>([]);
  const [matches, setMatches] = useState<VendorMatch[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [capabilities, setCapabilities] = useState<Capability[]>([]);
  const [rules, setRules] = useState<IntentRule[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);

  // Loading states
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Real signal ingestion (Triveny only). On mount we pull recent rows from
  //   • visitor_companies (intent-scored anonymous traffic identified to
  //     company level by Visitor ID),
  //   • icp company_matches (Lead Builder companies scored against this
  //     tenant's saved ICP profiles),
  // and map each into a BuyingSignal so the Signals tab is no longer
  // exclusively mock. Mocks remain as a tail of curated examples so the
  // demo experience stays rich even when those tables are empty.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [persistedRes, visitorsRes, matchesRes] = await Promise.all([
          marketingApi.getIntentSignals({ limit: 100 }).catch(() => null),
          visitorIdentificationApi
            .getCompanies({ page: 1, page_size: 25 })
            .catch(() => ({ success: false, data: null }) as const),
          leadBuilderApi
            .getCompanyMatches({ limit: 25 })
            .catch(() => ({ success: false, data: null }) as const),
        ]);
        if (cancelled) return;

        // Persisted signals (the real intent layer) come first.
        const persisted: BuyingSignal[] = (() => {
          const rows = (persistedRes as any)?.data ?? persistedRes ?? [];
          if (!Array.isArray(rows)) return [];
          return rows.map((r: any) => ({
            id: String(r.id),
            company: r.company || r.email || 'Unknown',
            signalType: r.signalType || 'website_visit',
            strength: r.strength || 'warm',
            category: r.category || '',
            timestamp: r.timestamp || '',
            source: r.source || 'Signals',
            details: r.details || '',
          })) as BuyingSignal[];
        })();

        const fromVisitors: BuyingSignal[] = (() => {
          const raw =
            visitorsRes && (visitorsRes as any).success && (visitorsRes as any).data
              ? (((visitorsRes as any).data as { companies?: VisitorCompany[] }).companies ?? [])
              : [];
          return raw.map((c) => mapVisitorCompanyToSignal(c));
        })();

        const fromMatches: BuyingSignal[] = (() => {
          const raw =
            matchesRes && (matchesRes as any).success && (matchesRes as any).data
              ? (() => {
                  const d = (matchesRes as any).data;
                  if (Array.isArray(d)) return d as CompanyMatch[];
                  if (Array.isArray(d?.matches)) return d.matches as CompanyMatch[];
                  if (Array.isArray(d?.company_matches)) return d.company_matches as CompanyMatch[];
                  return [] as CompanyMatch[];
                })()
              : [];
          return raw.map((m) => mapCompanyMatchToSignal(m));
        })();

        const real = [...persisted, ...fromVisitors, ...fromMatches].filter(
          (s) => s.company && s.company.trim().length > 0,
        );

        // Persisted signals lead; cap the feed to a reasonable size. When
        // there are no real signals the list stays empty and the Signals tab
        // shows its empty-state (no mock seed).
        setSignals(real.slice(0, 100));

        // Auto-populate the intent layer: persist the derived Visitor-ID / ICP
        // signals (server dedupes over 7 days + fires matching automations), so
        // intent flows into the funnel without a manual Capture. Fire-and-forget.
        const derived = [...fromVisitors, ...fromMatches].filter((s) => s.company && s.company.trim());
        if (derived.length) {
          marketingApi
            .syncIntentSignals(
              derived.slice(0, 50).map((s) => ({
                company: s.company,
                signal_type: s.signalType,
                score: s.strength === 'hot' ? 80 : s.strength === 'warm' ? 55 : 25,
                strength: s.strength,
                category: s.category,
                source: s.source || 'Auto-sync',
                details: s.details,
              })),
            )
            .catch(() => {});
        }
      } catch {
        // On failure the Signals list stays empty (empty-state), never mock.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isTriveny]);

  // Import Jobs — real Lead Builder jobs (discovery/enrichment/scoring runs).
  // Replaces the former hardcoded initialImportJobs. Empty list => empty-state.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await leadBuilderApi.getJobs({ limit: 50 } as any).catch(() => null);
        const rows: any[] = Array.isArray((res as any)?.data)
          ? (res as any).data
          : Array.isArray(res)
            ? (res as any)
            : [];
        if (cancelled || rows.length === 0) return;
        const mapped: ImportJob[] = rows.map((j: any) => {
          const total = Number(j.max_companies ?? j.companies_found ?? 0) || 0;
          const processed = Number(j.companies_found ?? j.companies_matched ?? 0) || 0;
          const statusMap: Record<string, ImportJobStatus> = {
            pending: 'pending', queued: 'pending', running: 'running',
            paused: 'pending', completed: 'completed', failed: 'failed', cancelled: 'cancelled',
          };
          return {
            id: String(j.id),
            dataSourceId: String(j.icp_profile_id ?? ''),
            dataSourceName: j.name || j.job_type || 'Lead Builder job',
            status: (statusMap[j.status] ?? 'pending') as ImportJobStatus,
            stage: (j.status === 'completed' ? 'completed' : j.status === 'failed' ? 'failed' : 'extraction') as ETLStage,
            progress: total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0,
            recordsProcessed: processed,
            recordsTotal: total,
            recordsFailed: 0,
            startedAt: j.started_at || j.created_at || '',
            completedAt: j.completed_at,
            errorMessage: j.error_message,
            logs: [],
          };
        });
        if (!cancelled) setImportJobs(mapped);
      } catch {
        /* empty-state on failure */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Data Sources — real connected providers/sources (Visitor-ID enrichment
  // providers + Lead Builder providers). Shows TRUE connection status instead
  // of the former hardcoded "Connected · 12,480 records" mock.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [enrichRes, lbProvRes] = await Promise.all([
          visitorIdentificationApi.getEnrichmentProviders().catch(() => null),
          leadBuilderApi.getProviderConfigs().catch(() => null),
        ]);
        const out: DataSource[] = [];
        const enrichRows: any[] = Array.isArray((enrichRes as any)?.data) ? (enrichRes as any).data : [];
        for (const p of enrichRows) {
          out.push({
            id: `enrich-${p.provider_name ?? p.id}`,
            name: p.display_name || p.provider_name || 'Enrichment provider',
            category: 'external-vendor',
            type: String(p.provider_name ?? 'enrichment'),
            description: `Visitor-ID enrichment · tier ${p.tier ?? '?'}`,
            status: p.is_enabled ? (p.api_key_set ? 'connected' : 'pending') : 'disconnected',
            recordsImported: Number(p.current_month_requests ?? 0) || 0,
            config: {},
          });
        }
        const lbRows: any[] = Array.isArray((lbProvRes as any)?.data) ? (lbProvRes as any).data : [];
        for (const p of lbRows) {
          out.push({
            id: `lb-${p.provider_name ?? p.id}`,
            name: p.provider_name || 'Lead Builder provider',
            category: 'external-vendor',
            type: String(p.provider_name ?? 'lead-builder'),
            description: `Lead Builder provider · tier ${p.provider_tier ?? '?'}`,
            status: p.is_enabled ? (p.is_verified ? 'connected' : 'pending') : 'disconnected',
            recordsImported: Number(p.total_requests ?? 0) || 0,
            config: {},
          });
        }
        if (!cancelled && out.length > 0) setDataSources(out);
      } catch {
        /* empty-state on failure */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Automation Rules — real marketing automations. Replaces initialRules mock.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await marketingApi.getAutomations?.().catch(() => null) ?? null;
        const rows: any[] = Array.isArray((res as any)?.data)
          ? (res as any).data
          : Array.isArray(res) ? (res as any) : [];
        if (cancelled || rows.length === 0) return;
        const toStrs = (v: any): string[] =>
          Array.isArray(v) ? v.map((x) => (typeof x === 'string' ? x : (x?.label ?? x?.name ?? JSON.stringify(x)))) : [];
        const mapped: IntentRule[] = rows.map((r: any) => ({
          id: String(r.id),
          name: r.name || 'Automation',
          trigger: r.trigger || r.trigger_event || r.description || '',
          conditions: toStrs(r.conditions),
          actions: toStrs(r.actions),
          status: (r.enabled ?? r.is_active) ? 'active' : 'paused',
          matches: Number(r.trigger_count ?? r.matches ?? 0) || 0,
        }));
        if (!cancelled) setRules(mapped);
      } catch {
        /* empty-state on failure */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Modal states
  const [createRuleOpen, setCreateRuleOpen] = useState(false);
  const [createMatchOpen, setCreateMatchOpen] = useState(false);
  const [viewSignalOpen, setViewSignalOpen] = useState(false);
  const [addVendorOpen, setAddVendorOpen] = useState(false);
  const [editVendorOpen, setEditVendorOpen] = useState(false);
  const [addCapabilityOpen, setAddCapabilityOpen] = useState(false);
  const [editCapabilityOpen, setEditCapabilityOpen] = useState(false);
  const [addDataSourceOpen, setAddDataSourceOpen] = useState(false);
  const [addDataSourceCategory, setAddDataSourceCategory] = useState<DataSourceCategory>('self-managed');
  const [viewImportJobOpen, setViewImportJobOpen] = useState(false);
  const [configureScheduleOpen, setConfigureScheduleOpen] = useState(false);

  // Selected items
  const [selectedSignal, setSelectedSignal] = useState<BuyingSignal | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<Vendor | null>(null);
  const [selectedCapability, setSelectedCapability] = useState<Capability | null>(null);
  const [selectedDataSource, setSelectedDataSource] = useState<DataSource | null>(null);
  const [selectedImportJob, setSelectedImportJob] = useState<ImportJob | null>(null);

  // Dynamic tabs based on organization mode
  const isSingleVendorMode = orgSettings.mode === 'single-vendor';

  const tabs: { id: IntelligenceTab; label: string; icon: React.ReactNode }[] = [
    // Signals - Intent Intelligence (inbound buying signals from prospects)
    { id: 'signals', label: t('platform.intelligence.tabs.signals', 'Signals'), icon: <TrendingUp className="h-4 w-4" /> },
    // Accounts - agentic account scoring (intent rolled up per company)
    { id: 'accounts', label: t('platform.intelligence.tabs.accounts', 'Accounts'), icon: <Building2 className="h-4 w-4" /> },
    // Opportunities - Program Intelligence (outbound market opportunities)
    { id: 'opportunities', label: t('platform.intelligence.tabs.opportunities', 'Opportunities'), icon: <Globe className="h-4 w-4" /> },
    // HIDDEN until backend endpoints exist (no vendors / capabilities /
    // vendor-match APIs yet — see Intelligence plan). Previously these tabs
    // were driven entirely by hardcoded mock arrays. Re-add here once the
    // endpoints ship:
    //   { id: 'matches', ... }  — signal↔capability/vendor matches
    //   { id: 'capabilities' | 'vendors', ... }  — catalog by org mode
    { id: 'analytics', label: t('platform.intelligence.tabs.analytics', 'Analytics'), icon: <BarChart3 className="h-4 w-4" /> },
    { id: 'settings', label: t('platform.intelligence.tabs.settings', 'Settings'), icon: <Settings className="h-4 w-4" /> },
  ];

  // Search state for the header search bar
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);

  const handleHeaderSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    setIsSearching(true);
    setShowSearchResults(true);
    // Real client-side search over loaded state — no more frozen mock.
    // Tokens are lower-cased and matched against each domain's salient
    // fields; relevance is the count of matched tokens × type-specific
    // weight (signals/opportunities surface higher than passive vendors).
    const tokens = query
      .toLowerCase()
      .split(/[\s,]+/)
      .map((t) => t.trim())
      .filter((t) => t.length >= 2);

    const matchTokens = (haystack: string): string[] => {
      const lower = haystack.toLowerCase();
      return tokens.filter((tok) => lower.includes(tok));
    };

    const results: SearchResult[] = [];

    // Signals — title is company, subtitle is strength + category + source
    for (const s of signals) {
      const haystack = [s.company, s.category, s.source, s.details, s.signalType].join(' | ');
      const matched = matchTokens(haystack);
      if (matched.length === 0) continue;
      results.push({
        id: `signal-${s.id}`,
        type: 'signal',
        title: s.company,
        subtitle: `${s.strength.toUpperCase()} · ${s.category} · ${s.source}`,
        relevanceScore: Math.min(100, 60 + matched.length * 15),
        matchedTerms: matched,
        data: { signalId: s.id },
      });
    }

    // Capabilities — title is name, subtitle is category + features count
    for (const c of capabilities) {
      const haystack = [c.name, c.description, c.category, ...c.features, ...c.useCases, ...c.industries].join(' | ');
      const matched = matchTokens(haystack);
      if (matched.length === 0) continue;
      results.push({
        id: `cap-${c.id}`,
        type: 'capability',
        title: c.name,
        subtitle: `${c.category} · ${c.signalMatches} signal matches · ${c.features.length} features`,
        relevanceScore: Math.min(100, 55 + matched.length * 12),
        matchedTerms: matched,
        data: { capabilityId: c.id },
      });
    }

    // Vendors — title is name, subtitle is category + capability summary
    for (const v of vendors) {
      const haystack = [v.name, v.category, ...v.capabilities].join(' | ');
      const matched = matchTokens(haystack);
      if (matched.length === 0) continue;
      results.push({
        id: `vendor-${v.id}`,
        type: 'vendor',
        title: v.name,
        subtitle: `${v.category} · ${v.capabilities.slice(0, 3).join(', ')}`,
        relevanceScore: Math.min(100, 50 + matched.length * 10),
        matchedTerms: matched,
        data: { vendorId: v.id },
      });
    }

    // Sort by relevance descending, cap to 25 visible.
    results.sort((a, b) => b.relevanceScore - a.relevanceScore);
    const top = results.slice(0, 25);

    // Tiny artificial delay so the spinner is visible — real search APIs
    // have latency too; users get a moment of "computing" before the list
    // appears. Keeps the UI honest.
    window.setTimeout(() => {
      setSearchResults(top);
      setIsSearching(false);
    }, 150);
  };

  // Handlers
  const handleCreateRule = (rule: Omit<IntentRule, 'id' | 'matches'>) => {
    const newRule: IntentRule = {
      ...rule,
      id: String(rules.length + 1),
      matches: 0,
    };
    setRules([newRule, ...rules]);
  };

  const handleToggleRule = (ruleId: string) => {
    setRules(rules.map(r =>
      r.id === ruleId ? { ...r, status: r.status === 'active' ? 'paused' : 'active' } : r
    ));
  };

  const handleDeleteRule = (ruleId: string) => {
    setRules(rules.filter(r => r.id !== ruleId));
  };

  const handleCreateMatch = (signalId: string, vendorId: string, notes: string) => {
    const signal = signals.find(s => s.id === signalId);
    const vendor = vendors.find(v => v.id === vendorId);
    if (signal && vendor) {
      const newMatch: VendorMatch = {
        id: String(matches.length + 1),
        signal,
        vendor,
        matchScore: Math.floor(Math.random() * 20) + 75,
        matchReasons: notes ? [notes] : ['Manual match created'],
        status: 'pending',
      };
      setMatches([newMatch, ...matches]);
    }
    setSelectedSignal(null);
  };

  const handleUpdateMatchStatus = (matchId: string, status: VendorMatch['status']) => {
    setMatches(matches.map(m =>
      m.id === matchId ? { ...m, status } : m
    ));
  };

  const handleRefreshSignals = async () => {
    setIsRefreshing(true);
    try {
      const res: any = await marketingApi.getIntentSignals({ limit: 100 });
      const rows = res?.data ?? res ?? [];
      if (Array.isArray(rows) && rows.length) {
        const persisted: BuyingSignal[] = rows.map((r: any) => ({
          id: String(r.id),
          company: r.company || r.email || 'Unknown',
          signalType: r.signalType || 'website_visit',
          strength: r.strength || 'warm',
          category: r.category || '',
          timestamp: r.timestamp || '',
          source: r.source || 'Signals',
          details: r.details || '',
        }));
        setSignals((prev) => {
          const ids = new Set(persisted.map((s) => s.id));
          return [...persisted, ...prev.filter((s) => !ids.has(s.id))].slice(0, 100);
        });
      }
    } catch {
      /* keep current list */
    } finally {
      setIsRefreshing(false);
    }
  };

  // Persist a signal into the funnel (fires matching automations server-side).
  const handleCaptureSignal = async (signal: BuyingSignal) => {
    const scoreByStrength = signal.strength === 'hot' ? 80 : signal.strength === 'warm' ? 55 : 25;
    try {
      const res: any = await marketingApi.createIntentSignal({
        company: signal.company,
        signal_type: signal.signalType,
        score: scoreByStrength,
        strength: signal.strength,
        category: signal.category,
        source: signal.source || 'Intelligence',
        details: signal.details,
      });
      const matched = res?.data?.matched_automations ?? res?.matched_automations ?? 0;
      alert(matched > 0
        ? `Signal captured — ${matched} automation(s) fired.`
        : 'Signal captured to the funnel. (No automation is set to trigger on this intent signal yet.)');
    } catch (e: any) {
      alert(e?.message || 'Failed to capture signal');
    }
  };

  const handleCreateMatchFromSignal = (signal: BuyingSignal) => {
    setSelectedSignal(signal);
    setCreateMatchOpen(true);
  };

  const handleViewSignal = (signal: BuyingSignal) => {
    setSelectedSignal(signal);
    setViewSignalOpen(true);
  };

  const handleAddVendor = (vendor: Omit<Vendor, 'id' | 'activeDeals'>) => {
    const newVendor: Vendor = {
      ...vendor,
      id: String(vendors.length + 1),
      activeDeals: 0,
    };
    setVendors([...vendors, newVendor]);
  };

  const handleEditVendor = (vendor: Vendor) => {
    setVendors(vendors.map(v => v.id === vendor.id ? vendor : v));
  };

  const handleDeleteVendor = (vendor: Vendor) => {
    if (confirm(`Are you sure you want to delete ${vendor.name}?`)) {
      setVendors(vendors.filter(v => v.id !== vendor.id));
    }
  };

  // Capability Handlers (for single-vendor mode)
  const handleAddCapability = (capability: Omit<Capability, 'id' | 'signalMatches'>) => {
    const newCapability: Capability = {
      ...capability,
      id: `cap-${Date.now()}`,
      signalMatches: 0,
    };
    setCapabilities([...capabilities, newCapability]);
  };

  const handleEditCapability = (capability: Capability) => {
    setCapabilities(capabilities.map(c => c.id === capability.id ? capability : c));
  };

  const handleDeleteCapability = (capability: Capability) => {
    if (confirm(`Are you sure you want to delete "${capability.name}"?`)) {
      setCapabilities(capabilities.filter(c => c.id !== capability.id));
    }
  };

  // Organization Settings Handlers
  const handleUpdateOrgSettings = (settings: Partial<OrganizationIntentSettings>) => {
    setOrgSettings(prev => ({ ...prev, ...settings }));
    // If mode changes, reset active tab if it was on a mode-specific tab
    if (settings.mode && settings.mode !== orgSettings.mode) {
      if (activeTab === 'vendors' || activeTab === 'capabilities') {
        setActiveTab('signals');
      }
    }
  };

  // Data Source Handlers
  const handleAddDataSource = (category: DataSourceCategory) => {
    setAddDataSourceCategory(category);
    setAddDataSourceOpen(true);
  };

  const handleCreateDataSource = (dataSource: Omit<DataSource, 'id' | 'recordsImported' | 'lastSync'>) => {
    const newDataSource: DataSource = {
      ...dataSource,
      id: `ds-${Date.now()}`,
      recordsImported: 0,
    };
    setDataSources([...dataSources, newDataSource]);

    // Simulate initial connection test
    setTimeout(() => {
      setDataSources(prev => prev.map(ds =>
        ds.id === newDataSource.id ? { ...ds, status: 'connected' as DataSourceStatus } : ds
      ));
    }, 2000);
  };

  const handleDeleteDataSource = (id: string) => {
    const source = dataSources.find(ds => ds.id === id);
    if (source && confirm(`Are you sure you want to delete ${source.name}?`)) {
      setDataSources(dataSources.filter(ds => ds.id !== id));
    }
  };

  const handleSyncDataSource = (id: string) => {
    // Update source to syncing status
    setDataSources(dataSources.map(ds =>
      ds.id === id ? { ...ds, status: 'syncing' as DataSourceStatus } : ds
    ));

    // Create a new import job
    const source = dataSources.find(ds => ds.id === id);
    if (source) {
      const newJob: ImportJob = {
        id: `job-${Date.now()}`,
        dataSourceId: id,
        dataSourceName: source.name,
        status: 'running',
        stage: 'extraction',
        progress: 0,
        recordsProcessed: 0,
        recordsTotal: Math.floor(Math.random() * 5000) + 1000,
        recordsFailed: 0,
        startedAt: new Date().toISOString(),
        logs: [
          { timestamp: new Date().toISOString(), level: 'info', message: 'Import job started', stage: 'extraction' },
        ],
      };
      setImportJobs([newJob, ...importJobs]);

      // Simulate job progress
      let progress = 0;
      const interval = setInterval(() => {
        progress += Math.random() * 20;
        if (progress >= 100) {
          progress = 100;
          clearInterval(interval);

          // Complete the job
          setImportJobs(prev => prev.map(j =>
            j.id === newJob.id ? {
              ...j,
              status: 'completed' as ImportJobStatus,
              stage: 'completed' as ETLStage,
              progress: 100,
              recordsProcessed: j.recordsTotal,
              completedAt: new Date().toISOString(),
              logs: [
                ...j.logs,
                { timestamp: new Date().toISOString(), level: 'info' as const, message: 'Import completed successfully', stage: 'completed' as ETLStage },
              ],
            } : j
          ));

          // Update data source
          setDataSources(prev => prev.map(ds =>
            ds.id === id ? {
              ...ds,
              status: 'connected' as DataSourceStatus,
              lastSync: new Date().toISOString(),
              recordsImported: ds.recordsImported + newJob.recordsTotal,
            } : ds
          ));
        } else {
          // Update job progress
          const stages: ETLStage[] = ['extraction', 'transformation', 'validation', 'loading'];
          const stageIndex = Math.min(Math.floor(progress / 25), 3);

          setImportJobs(prev => prev.map(j =>
            j.id === newJob.id ? {
              ...j,
              progress: Math.floor(progress),
              stage: stages[stageIndex],
              recordsProcessed: Math.floor((progress / 100) * j.recordsTotal),
            } : j
          ));
        }
      }, 500);
    }
  };

  const handleConfigureSchedule = (dataSource: DataSource) => {
    setSelectedDataSource(dataSource);
    setConfigureScheduleOpen(true);
  };

  const handleSaveSchedule = (dataSourceId: string, frequency: ImportSchedule['frequency']) => {
    // Calculate next run based on frequency
    const now = new Date();
    let nextRun: Date | undefined;

    switch (frequency) {
      case 'hourly':
        nextRun = new Date(now.getTime() + 60 * 60 * 1000);
        break;
      case 'daily':
        nextRun = new Date(now.getTime() + 24 * 60 * 60 * 1000);
        break;
      case 'weekly':
        nextRun = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        break;
      case 'monthly':
        nextRun = new Date(now.getFullYear(), now.getMonth() + 1, 1);
        break;
    }

    setDataSources(dataSources.map(ds =>
      ds.id === dataSourceId ? {
        ...ds,
        nextSync: nextRun?.toISOString(),
      } : ds
    ));
  };

  const handleViewImportJob = (job: ImportJob) => {
    setSelectedImportJob(job);
    setViewImportJobOpen(true);
  };

  return (
    <div
      className={isTriveny ? 'triveny-platform' : 'p-6 max-w-7xl mx-auto'}
      style={
        isTriveny
          ? { minHeight: '100vh', background: tvTokens.paper, fontFamily: tvTokens.fontSans, color: tvTokens.ink }
          : undefined
      }
    >
      {isTriveny && !embedded && (
        <AgentAssistantPanel
          agentKey="analytics.insights"
          title="Analytics assistant"
          greeting="Ask me about your funnel, campaign performance, or account scores and I'll pull the numbers."
          examples={['Summarize my funnel', 'Which campaigns are converting best?', 'Top accounts by intent score']}
        />
      )}
      {/* No page banner for Triveny: standalone the TvPillarBar ("ENGAGE · Intelligence")
          is the header; embedded in OutreachWorkspace the sub-tab navigator is. WeaveIn
          keeps its own legacy header below. */}
      <div className={isTriveny ? '' : 'mb-6'} style={isTriveny ? { padding: '20px 22px 0' } : undefined}>
        {!isTriveny && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-3">
              {!tabbed && (
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="h-6 w-6 text-purple-600" />
              </div>
              )}
              <div>
                {!tabbed && <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2"><Brain className="h-6 w-6 text-blue-600" />{t('platform.intelligence.title', 'Intelligence')}</h1>}
                <p className="text-gray-600 text-sm">
                  {t('platform.intelligence.subtitle', 'Unified view of market signals, opportunities, and insights')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Search Bar - Moved from tab to header */}
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('platform.intelligence.searchPlaceholder', "Search signals, opportunities, vendors... (e.g., 'organizations looking for supply chain replacement')")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleHeaderSearch(searchQuery)}
              onFocus={() => searchQuery.trim() && setShowSearchResults(true)}
              className="w-full pl-12 pr-24 py-3 text-base border border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white shadow-sm"
            />
            <button
              onClick={() => handleHeaderSearch(searchQuery)}
              disabled={!searchQuery.trim() || isSearching}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 px-4 py-1.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center gap-2 text-sm"
            >
              {isSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              {t('platform.intelligence.search.searchButton', 'Search')}
            </button>
          </div>

          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute z-50 top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-lg max-h-96 overflow-y-auto">
              <div className="p-3 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm text-gray-500">
                  {t('platform.intelligence.foundCount', 'Found {{count}} results', { count: searchResults.length })}
                </span>
                <button
                  onClick={() => setShowSearchResults(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <div className="divide-y divide-gray-100">
                {searchResults.slice(0, 5).map((result) => (
                  <div
                    key={result.id}
                    className="p-3 hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => {
                      setShowSearchResults(false);
                      // Navigate to appropriate tab based on result type
                      if (result.type === 'signal') setActiveTab('signals');
                      else if (result.type === 'opportunity') setActiveTab('opportunities');
                      else if (result.type === 'vendor') setActiveTab('vendors');
                      else if (result.type === 'capability') setActiveTab('capabilities');
                    }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-1.5 bg-gray-100 rounded">
                        {result.type === 'signal' && <TrendingUp className="h-4 w-4 text-blue-600" />}
                        {result.type === 'opportunity' && <Globe className="h-4 w-4 text-green-600" />}
                        {result.type === 'vendor' && <Building2 className="h-4 w-4 text-purple-600" />}
                        {result.type === 'capability' && <Layers className="h-4 w-4 text-orange-600" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{result.title}</p>
                        <p className="text-sm text-gray-500 truncate">{result.subtitle}</p>
                      </div>
                      <div className="text-right">
                        <span className="text-sm font-medium text-green-600">{result.relevanceScore}%</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {searchResults.length > 5 && (
                <div className="p-3 border-t border-gray-100 text-center text-sm text-gray-500">
                  {t('platform.intelligence.showingTopResults', 'Showing top 5 of {{count}} results. Refine your search for more specific results.', { count: searchResults.length })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Zap className="h-5 w-5 text-red-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.stats.hotSignals', 'Hot Signals')}</p>
              <p className="text-xl font-bold text-gray-900">
                {signals.filter(s => s.strength === 'hot').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <AlertCircle className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.stats.pendingMatches', 'Pending Matches')}</p>
              <p className="text-xl font-bold text-gray-900">
                {matches.filter(m => m.status === 'pending').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.stats.qualified', 'Qualified')}</p>
              <p className="text-xl font-bold text-gray-900">
                {matches.filter(m => m.status === 'qualified').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Target className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">{t('platform.intelligence.stats.activeVendors', 'Active Vendors')}</p>
              <p className="text-xl font-bold text-gray-900">{vendors.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="flex space-x-3 sm:space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab.id
                  ? 'border-purple-500 text-purple-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'signals' && (
        <SignalsTab
          signals={signals}
          onRefresh={handleRefreshSignals}
          onCreateMatch={handleCreateMatchFromSignal}
          onViewSignal={handleViewSignal}
          onCapture={handleCaptureSignal}
          isRefreshing={isRefreshing}
          capabilities={capabilities}
          orgSettings={orgSettings}
        />
      )}
      {activeTab === 'accounts' && <AccountScoresTab />}
      {activeTab === 'matches' && (
        <MatchesTab
          matches={matches}
          signals={signals}
          vendors={vendors}
          capabilities={capabilities}
          orgSettings={orgSettings}
          onCreateMatch={() => {
            setSelectedSignal(null);
            setCreateMatchOpen(true);
          }}
          onUpdateStatus={handleUpdateMatchStatus}
          onViewMatch={(match) => {
            setSelectedSignal(match.signal);
            setViewSignalOpen(true);
          }}
        />
      )}
      {activeTab === 'vendors' && (
        <VendorsTab
          vendors={vendors}
          onAddVendor={() => setAddVendorOpen(true)}
          onEditVendor={(vendor) => {
            setSelectedVendor(vendor);
            setEditVendorOpen(true);
          }}
          onDeleteVendor={handleDeleteVendor}
        />
      )}
      {activeTab === 'capabilities' && (
        <CapabilitiesTab
          capabilities={capabilities}
          organizationName={orgSettings.organizationName}
          onAddCapability={() => setAddCapabilityOpen(true)}
          onEditCapability={(capability) => {
            setSelectedCapability(capability);
            setEditCapabilityOpen(true);
          }}
          onDeleteCapability={handleDeleteCapability}
        />
      )}
      {activeTab === 'analytics' && <AnalyticsTab />}
      {activeTab === 'opportunities' && <OpportunitiesTab onNavigate={onNavigate} />}
      {activeTab === 'settings' && (
        <SettingsTab
          rules={rules}
          orgSettings={orgSettings}
          onCreateRule={() => setCreateRuleOpen(true)}
          onToggleRule={handleToggleRule}
          onDeleteRule={handleDeleteRule}
          onUpdateOrgSettings={handleUpdateOrgSettings}
        />
      )}

      {/* Modals */}
      <CreateRuleModal
        isOpen={createRuleOpen}
        onClose={() => setCreateRuleOpen(false)}
        onSubmit={handleCreateRule}
      />
      <CreateManualMatchModal
        isOpen={createMatchOpen}
        onClose={() => {
          setCreateMatchOpen(false);
          setSelectedSignal(null);
        }}
        signals={signals}
        vendors={vendors}
        onSubmit={handleCreateMatch}
        preselectedSignal={selectedSignal}
      />
      <ViewSignalModal
        isOpen={viewSignalOpen}
        onClose={() => {
          setViewSignalOpen(false);
          setSelectedSignal(null);
        }}
        signal={selectedSignal}
      />
      <AddVendorModal
        isOpen={addVendorOpen}
        onClose={() => setAddVendorOpen(false)}
        onSubmit={handleAddVendor}
      />
      <EditVendorModal
        isOpen={editVendorOpen}
        onClose={() => {
          setEditVendorOpen(false);
          setSelectedVendor(null);
        }}
        vendor={selectedVendor}
        onSubmit={handleEditVendor}
      />
      <AddCapabilityModal
        isOpen={addCapabilityOpen}
        onClose={() => setAddCapabilityOpen(false)}
        onSubmit={handleAddCapability}
      />
      <EditCapabilityModal
        isOpen={editCapabilityOpen}
        onClose={() => {
          setEditCapabilityOpen(false);
          setSelectedCapability(null);
        }}
        capability={selectedCapability}
        onSubmit={handleEditCapability}
      />
      <AddDataSourceModal
        isOpen={addDataSourceOpen}
        onClose={() => setAddDataSourceOpen(false)}
        onSubmit={handleCreateDataSource}
        category={addDataSourceCategory}
      />
      <ViewImportJobModal
        isOpen={viewImportJobOpen}
        onClose={() => {
          setViewImportJobOpen(false);
          setSelectedImportJob(null);
        }}
        job={selectedImportJob}
      />
      <ConfigureScheduleModal
        isOpen={configureScheduleOpen}
        onClose={() => {
          setConfigureScheduleOpen(false);
          setSelectedDataSource(null);
        }}
        dataSource={selectedDataSource}
        onSubmit={handleSaveSchedule}
      />
    </div>
  );
}

export default IntentIntelligencePage;
